// src/pages/WatchlistEvents.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { useBrandProtectionAccess } from "../utils/brandProtection";
import ExportConfigModal from "../components/ExportConfigModal";
import { ChevronDown, X, SlackIcon, Gavel, Copy, Check, Download, Plus, Eye, ArrowRight, ShieldCheck } from "lucide-react";
import { SiJira } from "react-icons/si";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import EventDetailSidebar from "../components/DetailSidebarEvents";
import IntelDetailSidebar from "../components/DetailSidebarIntel";
import {
  FilterBar,
  FilterDivider,
  FilterSegment,
  FilterMenu,
  FilterMenuItem,
  FilterReset,
  ActiveFilterRow,
} from "../components/FilterToolbar";

import {
  ensureAuthOrRedirect,
  redirectToLogin,
  attachExpiryTimer,
  getToken,
} from "../utils/auth";

const WATCHLISTS_API =
  "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_items_v2.php";
const EVENTS_API =
  "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_events_v3.php";
const LOOKALIKE_DETAIL_API =
  "https://whiteintel.io/api/regular/app/v2/watchlist/lookalike_detail.php";
const TAKEDOWN_REQUESTS_API =
  "https://whiteintel.io/api/regular/app/v2/watchlist/takedown_requests.php";
const LOOKALIKES_EXPORT_API =
  "https://whiteintel.io/api/regular/app/v2/watchlist/lookalikes_export.php";
const TAKEDOWN_CREDITS_API =
  "https://whiteintel.io/api/regular/app/v2/watchlist/takedown_credits.php";
const REQUEST_TAKEDOWN_API =
  "https://whiteintel.io/api/regular/app/v2/watchlist/request_takedown.php";
// Adjust if you have a real endpoint for bulk resolve:
const BULK_RESOLVE_API =
  "https://whiteintel.io/api/regular/app/v2/watchlist/bulk_mark_resolved.php";

const INTEL_API =
  "https://whiteintel.io/api/regular/app/v2/threatfeed/get_feed.php"; // feed + detail + timeseries

const frosted =
  "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)]";

const tagClass = (t) => {
  switch (t) {
    case "stealer":
      return "border-red-500/30 text-red-200 bg-red-500/10";
    case "combolist":
      return "border-amber-500/30 text-amber-200 bg-amber-500/10";
    case "consumer":
      return "border-sky-500/30 text-sky-200 bg-sky-500/10";
    case "corporate":
      return "border-emerald-500/30 text-emerald-200 bg-emerald-500/10";
    case "open":
      return "border-blue-500/30 text-blue-200 bg-blue-500/10";
    case "resolved":
      return "border-emerald-500/30 text-emerald-200 bg-emerald-500/10";
    case "unresolved":
      return "border-orange-500/30 text-orange-200 bg-orange-500/10";
    default:
      return "border-neutral-700 text-neutral-200 bg-neutral-900/40";
  }
};

// --------- formatting helpers ----------
const fmtISO = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "—");
const prettyName = (s = "") =>
  s
    .split(".")
    .map((p, i, arr) =>
      i === arr.length - 1 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1)
    )
    .join(".");

const audienceLabel = (breachType) => {
  if (breachType === "consumer") return "Consumer Account Credentials";
  if (breachType === "corporate") return "Corporate User Credentials";
  if (breachType === "lookalike") return "Look-alike Domain(s)";
  
  if (breachType === "mention") return "Dark web Mention";
  if (breachType === "git_leak") return "Exposed Secret(s)";
  return "User Credentials";
};

function buildEventTitle(row) {
  const audience = audienceLabel(row.breach_type);
  const subject = row.entry_type === "domain" ? prettyName(row.entry) : row.entry;
  let actionPhrase;
  if (row.event_type === "combolist") {
    actionPhrase = "Identified on Underground Forums or Messaging Platforms";
  } else if (row.event_type === "stealer") {
    actionPhrase = "Compromised via Malware-Infected Device";
  } else if (row.event_type === "intel" || row.event_type === "darkweb_mention" || row.event_type === "git_leak" || row.event_type=== "exposed_secret") {
    actionPhrase = "Detected For";
  } else {
    actionPhrase = "Exposed in the Cybercrime Ecosystem";
  }
  return `${audience} ${actionPhrase} – ${subject}`;
}

function toUiEvent(row) {
  return {
    id: row.id,
    title: buildEventTitle(row),
    created_at_iso: row.event_date || row.created_at || "",
    credentials_count: Number(row.credentials_count || 0),
    domain: row.entry_type === "domain" ? row.entry : undefined,
    email: row.entry_type === "email" ? row.entry : undefined,
    tags: [row.event_type, row.breach_type, row.status].filter(Boolean),
    min_log_date: row.min_log_date,
    max_log_date: row.max_log_date,
    breach_type: row.breach_type,
    events_count: row.event_count,
    // ensure mention intel post id is carried to UI
    mention_event_id: row.mention_event_id || row.post_id || null,
  };
}

// "leaks" is a UI grouping; the backend filters by literal event_type values.
// Translate at payload-build time so the request hits both stealer + combolist.
const resolveEventTypeForApi = (bt) =>
  bt === "leaks" ? "stealer,combolist" : bt;

// --------- date/range helpers ----------
const getDateDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split("T")[0]; // YYYY-MM-DD
};

const resolveRange = (quick) => {
  const today = new Date().toISOString().split("T")[0];
  if (quick === "7d") return { date_from: getDateDaysAgo(7), date_to: today };
  if (quick === "30d") return { date_from: getDateDaysAgo(30), date_to: today };
  if (quick === "90d") return { date_from: getDateDaysAgo(90), date_to: today };
  return { date_from: getDateDaysAgo(720), date_to: today }; // "all" -> 180d window
};

// Trim leading/trailing empty buckets
const trimZeroEdges = (series) => {
  if (!Array.isArray(series) || series.length === 0) return [];
  let first = 0;
  while (first < series.length && (series[first]?.count ?? 0) === 0) first++;
  let last = series.length - 1;
  while (last >= first && (series[last]?.count ?? 0) === 0) last--;
  return series.slice(first, last + 1);
};

export default function WatchlistEvents({ mode = "events" }) {
  const isBrandMode = mode === "brand";
  const navigate = useNavigate();
  const brandAccess = useBrandProtectionAccess();
  const brandGateActive = 
  isBrandMode && !brandAccess.loading && brandAccess.available === false;
  // layout
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [panel, setPanel] = useState(null); // "detail" | "mention" | null
  const [selectedPostId, setSelectedPostId] = useState(null);

  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Filters
  const [dataType, setDataType] = useState("all"); // corporate|consumer|all
  const [breachType, setBreachType] = useState(isBrandMode ? "intel" : "all"); // stealer|combolist|all
  // Which tab is visually selected. Decoupled from `breachType` so that
  // selecting an Event Type filter on the All tab doesn't move the user to
  // a different tab — the filter just refines the All view.
  const [currentTab, setCurrentTab] = useState(isBrandMode ? "intel" : "all"); // all|leaks|intel|darkweb_mention|exposed_secret
  const [q, setQ] = useState("");
  const [dateQuick, setDateQuick] = useState("All");
  const [perBucket, setPerBucket] = useState("month");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "open" | "resolved" | "unresolved"
  const [takedownStatus, setTakedownStatus] = useState("all"); // client-side status filter for takedown table

  // Watchlist domains dropdown state
  const [wlOpen, setWlOpen] = useState(false);
  const [watchlistQueries, setWatchlistQueries] = useState([]); // array of strings
  const [watchlistEntries, setWatchlistEntries] = useState([]); // [{entry, entry_type}]
  const [selectedWatchlist, setSelectedWatchlist] = useState(null); // string | null
  const [wlLoading, setWlLoading] = useState(true);
  const [wlError, setWlError] = useState("");

  const [exportOpen, setExportOpen] = useState(false);

  // data - list
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState(null);

  // data - chart
  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);

  // data - lookalike list (only used when the Look-alike tab is active)
  const [lookalikes, setLookalikes] = useState([]);
  const [lookalikesLoading, setLookalikesLoading] = useState(false);
  const [lookalikesError, setLookalikesError] = useState(null);

  // Lookalike screenshot preview lightbox
  const [previewImage, setPreviewImage] = useState(null); // { url, domain } | null

  useEffect(() => {
    if (!previewImage) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPreviewImage(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [previewImage]);

  // Lookalike detail sidebar (per-row drill-in with DNS / WHOIS / SSL / etc.)
  const [lookalikeKvInline, setLookalikeKvInline] = useState(null);
  const [lookalikeDetailFull, setLookalikeDetailFull] = useState(null);
  const [lookalikeDetailLoading, setLookalikeDetailLoading] = useState(false);
  const [lookalikeDetailError, setLookalikeDetailError] = useState(null);

  const openLookalikeDetail = (row) => {
    setLookalikeKvInline(row);
    setLookalikeDetailFull(null);
    setLookalikeDetailError(null);
    if (row?.id) fetchLookalikeDetail(row.id);
  };
  const closeLookalikeDetail = () => {
    setLookalikeKvInline(null);
    setLookalikeDetailFull(null);
    setLookalikeDetailError(null);
  };

  const fetchLookalikeDetail = async (id) => {
    setLookalikeDetailLoading(true);
    setLookalikeDetailError(null);
    try {
      const res = await fetch(LOOKALIKE_DETAIL_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        cache: "no-store",
        body: JSON.stringify({ id }),
      });
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      if (!res.ok) throw new Error(`Detail error: ${res.status}`);
      const data = await res.json();
      setLookalikeDetailFull(data?.detail || data?.row || data || null);
    } catch (e) {
      setLookalikeDetailError(e?.message || "Failed to load detail");
    } finally {
      setLookalikeDetailLoading(false);
    }
  };

  useEffect(() => {
    if (!lookalikeKvInline) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLookalikeDetail();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookalikeKvInline]);
  const [lookalikesPage, setLookalikesPage] = useState(1);
  const [lookalikesHasMore, setLookalikesHasMore] = useState(false);
  const lookalikesLoadingMoreRef = useRef(false);

  const isLookalikeTab = currentTab === "intel";
  // Sub-tab inside Look-alikes: "all" | "tracked" | "takedown".
  const [lookalikeSubTab, setLookalikeSubTab] = useState("all");

  // Takedown Requests subtab data
  const [takedownRequests, setTakedownRequests] = useState([]);
  const [takedownRequestsLoading, setTakedownRequestsLoading] = useState(false);
  const [takedownRequestsError, setTakedownRequestsError] = useState(null);
  const [takedownRequestsSummary, setTakedownRequestsSummary] = useState(null);
  const [takedownRefreshKey, setTakedownRefreshKey] = useState(0);

  // Takedown credits visible on the Takedown Requests subtab.
  const [pageTakedownCredits, setPageTakedownCredits] = useState(null);

  // Detail side panel for a single takedown request row.
  const [selectedTakedown, setSelectedTakedown] = useState(null);
  useEffect(() => {
    if (!selectedTakedown) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSelectedTakedown(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedTakedown]);

  // CSV export of the look-alike list.
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportScope, setExportScope] = useState("all"); // "all" | "selected"
  const [exportSelectedEntries, setExportSelectedEntries] = useState([]);
  const [exportDateMode, setExportDateMode] = useState("all"); // "all" | "7d" | "30d" | "90d"
  const [exportStatus, setExportStatus] = useState("all");

  // Manual takedown (user-supplied domain) modal state.
  const [manualTakedownOpen, setManualTakedownOpen] = useState(false);
  const [manualTakedownDomain, setManualTakedownDomain] = useState("");
  const [manualTakedownEntry, setManualTakedownEntry] = useState("");
  const [manualTakedownNote, setManualTakedownNote] = useState("");
  const [manualTakedownSubmitting, setManualTakedownSubmitting] = useState(false);
  const [manualTakedownStatus, setManualTakedownStatus] = useState(null);

  // Interrupt modal shown when the user tries to file a takedown with 0 credits.
  const [outOfCreditsOpen, setOutOfCreditsOpen] = useState(false);
  // Reset sub-tab when leaving the Look-alikes tab so users don't return to a
  // stale subtab next time they open it.
  useEffect(() => {
    if (!isLookalikeTab) setLookalikeSubTab("all");
  }, [isLookalikeTab]);

  // Fetch takedown requests when the user enters the Takedown Requests subtab.
  useEffect(() => {
    if (!isLookalikeTab || lookalikeSubTab !== "takedown") return;
    const ctrl = new AbortController();
    (async () => {
      setTakedownRequestsLoading(true);
      setTakedownRequestsError(null);
      try {
        const res = await fetch(TAKEDOWN_REQUESTS_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          cache: "no-store",
          body: JSON.stringify({ page: 1, per_page: 50 }),
          signal: ctrl.signal,
        });
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }
        if (!res.ok) throw new Error(`Takedowns error: ${res.status}`);
        const data = await res.json();
        if (data?.success === false) {
          throw new Error(data?.error || "Failed to load takedown requests");
        }
        setTakedownRequests(Array.isArray(data?.takedowns) ? data.takedowns : []);
        setTakedownRequestsSummary(data?.summary || null);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setTakedownRequestsError(e?.message || "Failed to load takedown requests");
      } finally {
        setTakedownRequestsLoading(false);
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLookalikeTab, lookalikeSubTab, takedownRefreshKey]);

  // Fetch takedown credits for the page-level pill on the Takedown Requests subtab.
  const fetchPageTakedownCredits = async () => {
    try {
      const res = await fetch(TAKEDOWN_CREDITS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        cache: "no-store",
        body: JSON.stringify({}),
      });
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        return;
      }
      const data = await res.json().catch(() => null);
      if (data && typeof data.credits_remaining === "number") {
        setPageTakedownCredits(data.credits_remaining);
      }
    } catch {
      /* best-effort */
    }
  };
  useEffect(() => {
    if (isLookalikeTab && lookalikeSubTab === "takedown") {
      fetchPageTakedownCredits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLookalikeTab, lookalikeSubTab]);

  // Open the export modal, seeding sensible defaults from current toolbar state.
  const openExportModal = () => {
    setExportScope(selectedWatchlist ? "selected" : "all");
    setExportSelectedEntries(selectedWatchlist ? [selectedWatchlist] : []);
    const seedMode =
      dateQuick === "7d" ? "7d" :
      dateQuick === "30d" ? "30d" :
      dateQuick === "90d" ? "90d" : "all";
    setExportDateMode(seedMode);
    setExportStatus(statusFilter || "all");
    setExportModalOpen(true);
  };

  const closeExportModal = () => {
    if (exportingCsv) return;
    setExportModalOpen(false);
  };

  // CSV export driven by the export modal state.
  const handleExportCsv = async () => {
    if (exportingCsv) return;
    if (exportScope === "selected" && exportSelectedEntries.length === 0) return;

    setExportingCsv(true);
    try {
      const payload = { format: "csv" };
      payload.range =
        exportDateMode === "7d" ? "last7d" :
        exportDateMode === "30d" ? "last30d" :
        exportDateMode === "90d" ? "last90d" : "all";
      if (exportScope === "selected") {
        payload.entries = exportSelectedEntries;
      }
      if (exportStatus !== "all") payload.status = exportStatus;

      const res = await fetch(LOOKALIKES_EXPORT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify(payload),
      });
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        return;
      }
      if (!res.ok) throw new Error(`Export HTTP ${res.status}`);

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `lookalikes_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportModalOpen(false);
    } catch (e) {
      console.error("CSV export failed", e);
    } finally {
      setExportingCsv(false);
    }
  };

  const toggleExportEntry = (entry) => {
    setExportSelectedEntries((prev) =>
      prev.includes(entry) ? prev.filter((e) => e !== entry) : [...prev, entry]
    );
  };

  const resetManualTakedownForm = () => {
    setManualTakedownDomain("");
    setManualTakedownEntry("");
    setManualTakedownNote("");
    setManualTakedownStatus(null);
  };

  const closeManualTakedown = () => {
    if (manualTakedownSubmitting) return;
    setManualTakedownOpen(false);
    resetManualTakedownForm();
  };

  const handleSubmitManualTakedown = async () => {
    const domain = manualTakedownDomain.trim();
    const note = manualTakedownNote.trim();
    if (!domain || !note || manualTakedownSubmitting) return;
    if (typeof pageTakedownCredits === "number" && pageTakedownCredits <= 0) {
      setManualTakedownStatus({
        type: "error",
        message: "No takedown credits remaining. Contact support to top up.",
      });
      return;
    }
    setManualTakedownSubmitting(true);
    setManualTakedownStatus(null);
    try {
      const res = await fetch(REQUEST_TAKEDOWN_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({
          domain,
          original_entry: manualTakedownEntry.trim() || undefined,
          note,
          manual: true,
        }),
      });
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      const raw = await res.text();
      let result = {};
      try { result = raw ? JSON.parse(raw) : {}; } catch { result = {}; }
      if (typeof result.credits_remaining === "number") {
        setPageTakedownCredits(result.credits_remaining);
      }
      const ok = res.ok && (result.status === "success" || result.success === true);
      if (!ok) {
        setManualTakedownStatus({
          type: "error",
          message: result?.error || result?.message || `Failed to file takedown (HTTP ${res.status}).`,
        });
        return;
      }
      setManualTakedownStatus({
        type: "success",
        message: result.message || "Takedown request filed.",
      });
      // Refresh the table so the new request appears immediately.
      setTakedownRefreshKey((k) => k + 1);
      fetchPageTakedownCredits();
      setTimeout(() => {
        setManualTakedownOpen(false);
        resetManualTakedownForm();
      }, 900);
    } catch (e) {
      setManualTakedownStatus({
        type: "error",
        message: e?.message || "Failed to file takedown",
      });
    } finally {
      setManualTakedownSubmitting(false);
    }
  };

  // URL <-> tab sync: read on mount, write on change. Lets users bookmark or
  // refresh on a specific tab/subtab.
  const VALID_TABS = isBrandMode
    ? ["intel"]
    : ["all", "leaks", "darkweb_mention", "exposed_secret"];
  const VALID_SUBTABS = ["all", "takedown"];
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t && VALID_TABS.includes(t)) {
      setCurrentTab(t);
      setBreachType(t);
    }
    const sub = params.get("sub");
    if (sub && VALID_SUBTABS.includes(sub)) {
      setLookalikeSubTab(sub);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (currentTab && currentTab !== "all") {
      params.set("tab", currentTab);
    } else {
      params.delete("tab");
    }
    if (currentTab === "intel" && lookalikeSubTab && lookalikeSubTab !== "all") {
      params.set("sub", lookalikeSubTab);
    } else {
      params.delete("sub");
    }
    const qs = params.toString();
    const newUrl =
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }, [currentTab, lookalikeSubTab]);

  // bulk actions
  const [exporting, setExporting] = useState(false);

  // guards/refs
  const loadMoreRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const lastListKeyRef = useRef("");
  const lastChartKeyRef = useRef("");
  const inflightKeyRef = useRef("");

  const openEvent = (ev) => {
    setSelectedEvent(ev);
    const isMention = ev && String(ev.breach_type || "").toLowerCase() === "mention";
    if (isMention) {
      const pid = ev.mention_event_id || ev.post_id || ev.id || null;
      setSelectedPostId(pid);
      setPanel("mention");
    } else {
      setPanel("detail");
    }
  };

  const closePanel = () => {
    setPanel(null);
    // optionally clear selection:
    // setSelectedEvent(null);
    // setSelectedPostId(null);
  };

  async function setEventStatus(id, status = "resolved") {
    const res = await fetch(
      "https://whiteintel.io/api/regular/app/v2/watchlist/set_event_status.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({ id, status }),
      }
    );

    if ([401, 419, 440].includes(res.status)) {
      redirectToLogin();
      throw new Error(`AUTH_REDIRECT_${res.status}`);
    }

    if (!res.ok) throw new Error(`status HTTP ${res.status}`);
  }

  function handleStatusChange(eventId, newStatus) {
    setEvents((prev) =>
      prev.map((e) =>
        e.id === eventId
          ? {
              ...e,
              status: newStatus,
              tags: [
                ...e.tags.filter((t) => t !== "open" && t !== "resolved" && t !== "unresolved"),
                newStatus,
              ],
            }
          : e
      )
    );
  }

  const handleExportConfigured = async ({ domain, range, markResolved }) => {
    setExportOpen(true);
  };

  function getJwtToken() {
    try {
      return (
        localStorage.getItem("jwtToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  const resetListFilters = () => {
    setDataType("all");
    setBreachType("all");
    setSelectedWatchlist(null);
    setStatusFilter("all");
  };

  // Load watchlist domains (safe with StrictMode via AbortController)
  useEffect(() => {
    const ctrl = new AbortController();
    setWlLoading(true);
    setWlError("");

    ensureAuthOrRedirect();
    attachExpiryTimer();

    // Tabs that filter watchlist entries by type can pull a smaller list
    // directly from the backend rather than fetching everything and filtering
    // client-side.
    const metric =
      currentTab === "intel"
        ? "domains"
        : currentTab === "darkweb_mention"
        ? "keywords"
        : "watchlist_domains";

    (async function loadWatchlistDomains() {
      try {
        const jwtToken = getJwtToken();
        const resp = await fetch(WATCHLISTS_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {}),
          },
          cache: "no-store",
          body: JSON.stringify({ metric }),
          signal: ctrl.signal,
        });
        if (!resp.ok) throw new Error(`Request failed: ${resp.status}`);

        const data = await resp.json();
        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];
        const fullEntries = items
          .map((x) => {
            const entry = typeof x?.entry === "string" ? x.entry.trim() : null;
            if (!entry) return null;
            return { entry, entry_type: x?.entry_type || null };
          })
          .filter(Boolean);
        // Dedup by entry string, sort alphabetically.
        const seen = new Set();
        const uniqueEntries = [];
        for (const e of fullEntries) {
          if (seen.has(e.entry)) continue;
          seen.add(e.entry);
          uniqueEntries.push(e);
        }
        uniqueEntries.sort((a, b) => a.entry.localeCompare(b.entry));
        if (!ctrl.signal.aborted) {
          setWatchlistEntries(uniqueEntries);
          setWatchlistQueries(uniqueEntries.map((e) => e.entry));
        }
      } catch (e) {
        if (!ctrl.signal.aborted) setWlError(e?.message || "Unable to load watchlist domains");
      } finally {
        if (!ctrl.signal.aborted) setWlLoading(false);
      }
    })();

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  // If the previously selected identifier isn't in the newly-fetched list
  // (e.g. user had a domain selected then switched to the Mentions tab which
  // returns keywords only), drop the selection so it doesn't silently filter
  // results to an entry that isn't even in the dropdown.
  useEffect(() => {
    if (!selectedWatchlist) return;
    if (watchlistEntries.length === 0) return;
    const stillThere = watchlistEntries.some((e) => e.entry === selectedWatchlist);
    if (!stillThere) setSelectedWatchlist(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlistEntries]);

  // client search filter
  const filtered = useMemo(() => {
    if (!q.trim()) return events;
    const needle = q.trim().toLowerCase();
    return events.filter((e) => {
      const hay = `${e.title ?? ""} ${e.email ?? ""} ${e.domain ?? ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [events, q]);

  // ---- fetch lookalike domain list (Look-alike tab only) ----
  const fetchLookalikes = async ({ append = false, pageOverride } = {}) => {
    if (append && (!lookalikesHasMore || lookalikesLoadingMoreRef.current)) return;
    const nextPage = append ? (pageOverride ?? lookalikesPage + 1) : 1;

    if (append) lookalikesLoadingMoreRef.current = true;
    else setLookalikesLoading(true);
    setLookalikesError(null);

    try {
      // Map the dateQuick toolbar value to the backend's `range` param.
      // dateQuick is one of: "7d" | "30d" | "90d" | "All".
      const rangeParam =
        dateQuick === "7d"
          ? "last7d"
          : dateQuick === "30d"
          ? "last30d"
          : dateQuick === "90d"
          ? "last90d"
          : "all";
      const payload = {
        mode: "list_lookalikes",
        page: nextPage,
        per_page: 50,
        range: rangeParam,
      };
      if (selectedWatchlist) payload.entry = selectedWatchlist;
      if (statusFilter !== "all") payload.status = statusFilter;

      const res = await fetch(EVENTS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        cache: "no-store",
        body: JSON.stringify(payload),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      if (!res.ok) throw new Error(`List error: ${res.status}`);
      const data = await res.json();

      const rows = data?.rows || data?.results || [];
      setLookalikes((prev) => (append ? [...prev, ...rows] : rows));
      setLookalikesPage(data?.page ?? nextPage);
      setLookalikesHasMore(Boolean(data?.has_more));
    } catch (e) {
      setLookalikesError(e?.message || "Failed to fetch lookalikes");
    } finally {
      setLookalikesLoading(false);
      lookalikesLoadingMoreRef.current = false;
    }
  };

  // ---- fetch list page ----
  const fetchList = async ({ append = false, pageOverride } = {}) => {
    if (append && (!hasMore || loadingMoreRef.current)) return;

    const { date_from, date_to } = resolveRange(dateQuick);
    const nextPage = append ? (pageOverride ?? page + 1) : 1;

    // dedupe identical in-flight requests
    const reqKey = JSON.stringify({
      date_from,
      date_to,
      breach_type: dataType,
      event_type: resolveEventTypeForApi(breachType),
      status: statusFilter !== "all" ? statusFilter : null,
      entry: selectedWatchlist || null,
      page: nextPage,
    });
    if (inflightKeyRef.current === reqKey) return;
    inflightKeyRef.current = reqKey;

    loadingMoreRef.current = true;
    setLoadingList(true);

    try {
      const payload = {
        date_from,
        date_to,
        breach_type: dataType, // corporate|consumer|all
        event_type: resolveEventTypeForApi(breachType), // stealer|combolist|all
        per_page: 50,
        page: nextPage,
      };
      if (selectedWatchlist) payload.entry = selectedWatchlist;
      if (statusFilter !== "all") payload.status = statusFilter;

      const res = await fetch(EVENTS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        cache: "no-store",
        body: JSON.stringify(payload),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      if (!res.ok) throw new Error(`List error: ${res.status}`);
      const data = await res.json();

      const rows = (data?.results || []).map(toUiEvent);
      setEvents((prev) => (append ? [...prev, ...rows] : rows));
      setPage(data?.page ?? nextPage);
      setHasMore(Boolean(data?.has_more));
    } catch (e) {
      setError(e?.message || "Failed to fetch events");
    } finally {
      setLoadingList(false);
      loadingMoreRef.current = false;
      inflightKeyRef.current = "";
    }
  };

  // ---- fetch chart series ----
  const fetchChart = async () => {
    setLoadingChart(true);
    try {
      const { date_from, date_to } = resolveRange(dateQuick);
      const res = await fetch(EVENTS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        cache: "no-store",
        body: JSON.stringify({
          date_from,
          date_to,
          mode: "timeseries",
          per: perBucket, // "day" | "week" | "month"
          entry: selectedWatchlist,
        }),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      if (!res.ok) throw new Error(`Chart error: ${res.status}`);
      const data = await res.json();

      const series = (data?.buckets || []).map((b) => ({
        label: b.label,
        count: b.count,
      }));
      setChartData(trimZeroEdges(series));
    } catch (e) {
      setError(e?.message || "Failed to fetch chart");
    } finally {
      setLoadingChart(false);
    }
  };

  // initial + whenever filters change: reset list & fetch page 1
  useEffect(() => {
    const key = JSON.stringify({
      dateQuick,
      dataType,
      breachType,
      statusFilter,
      selectedWatchlist: selectedWatchlist || null,
    });
    if (lastListKeyRef.current === key) return; // avoid StrictMode double-run
    lastListKeyRef.current = key;

    setPage(1);
    setHasMore(true);
    setEvents([]);
    if (isLookalikeTab) {
      setLookalikesPage(1);
      setLookalikesHasMore(false);
      setLookalikes([]);
      fetchLookalikes({ append: false, pageOverride: 1 });
    } else {
      fetchList({ append: false, pageOverride: 1 });
      fetchChart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateQuick, dataType, breachType, statusFilter, selectedWatchlist]);

  // chart fetch on range/bucket change
  useEffect(() => {
    const key = JSON.stringify({ dateQuick, perBucket });
    if (lastChartKeyRef.current === key) return;

    lastChartKeyRef.current = key;
    fetchChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateQuick, perBucket]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current) return;
    if (!hasMore || loadingMoreRef.current || loadingList) return;
    if (events.length === 0) return; // wait until page 1 loaded

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !loadingMoreRef.current) {
          fetchList({ append: true });
        }
      },
      { root: null, rootMargin: "300px", threshold: 0 }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingList, events.length]);

  // --------- bulk export ---------
  const handleExportAll = async () => {
    setExporting(true);
    try {
      const { date_from, date_to } = resolveRange(dateQuick);
      let pageNum = 1;
      const perPage = 500;
      const all = [];

      // Pull all pages under current filters (hard cap)
      for (let i = 0; i < 200; i++) {
        const payload = {
          date_from,
          date_to,
          breach_type: dataType,
          event_type: resolveEventTypeForApi(breachType),
          entry: selectedWatchlist || undefined,
          per_page: perPage,
          page: pageNum,
        };

        const res = await fetch(EVENTS_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          cache: "no-store",
          body: JSON.stringify(payload),
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        if (!res.ok) throw new Error(`Export fetch error: ${res.status}`);

        const data = await res.json();
        const rows = Array.isArray(data?.results) ? data.results : [];
        all.push(...rows);
        if (!data?.has_more || rows.length === 0) break;
        pageNum += 1;
      }

      // Build CSV
      const headers = [
        "id",
        "event_date",
        "entry_type",
        "entry",
        "event_type",
        "breach_type",
        "status",
        "credentials_count",
        "min_log_date",
        "max_log_date",
        "created_at",
      ];
      const escape = (v) => {
        if (v === null || v === undefined) return "";
        const s = String(v);
        if (/[\",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };
      const csv =
        headers.join(",") +
        "\n" +
        all
          .map((r) =>
            [
              r.id,
              r.event_date || r.created_at || "",
              r.entry_type || "",
              r.entry || "",
              r.event_type || "",
              r.breach_type || "",
              r.status || "",
              r.credentials_count ?? "",
              r.min_log_date || "",
              r.max_log_date || "",
              r.created_at || "",
            ]
              .map(escape)
              .join(",")
          )
          .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const nameParts = [
        "events",
        dateQuick,
        dataType,
        breachType,
        selectedWatchlist ? selectedWatchlist.replace(/\W+/g, "_") : "all",
      ];
      a.href = url;
      a.download = `${nameParts.filter(Boolean).join("_")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.message || "Failed to export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Left-aligned header */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">
              {isBrandMode ? "Brand Protection" : "Watchlist Events"}
            </h1>
          </div>
        </div>
      </header>

      {/* Sub-nav strip — lives between header and the flex layout so its
          underline spans full content width (sidebar-offset → viewport right),
          matching the Threat Feeds tab strip pattern. Hidden in brand mode
          (which uses look-alike sub-tabs inside main instead). */}
      {!isBrandMode && !brandGateActive && (
        <div
          className="border-b border-white/10 bg-[#000414]"
          style={{ marginLeft: collapsed ? "56px" : "224px" }}
        >
          <div className="px-6 py-2">
            <nav className="flex items-center gap-8 flex-wrap" role="tablist" aria-label="Event types">
              {[
                { key: "all", label: "All" },
                { key: "leaks", label: "Leaks" },
                { key: "darkweb_mention", label: "Mentions" },
                { key: "exposed_secret", label: "Exposed Secrets" },
              ].map((opt) => {
                const active = currentTab === opt.key;
                return (
                  <button
                    key={opt.key}
                    role="tab"
                    aria-selected={active}
                    onClick={() => {
                      setCurrentTab(opt.key);
                      setBreachType(opt.key);
                    }}
                    className={`py-2 text-sm font-medium transition whitespace-nowrap -mb-px border-b-2 ${
                      active
                        ? "text-white border-sky-500"
                        : "text-neutral-400 hover:text-white border-transparent"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey={isBrandMode ? "brand" : "events"}
          onChange={() => {}}
        />

        {/* Content */}
        <main className="flex-1 min-w-0 px-4 md:px-6 pt-4 pb-8">
          {brandGateActive && (
            <div className="max-w-2xl mx-auto mt-12">
              <div className={`rounded-md ${frosted} p-8 text-center`}>
                <div className="mx-auto inline-flex rounded-full border border-sky-500/30 bg-sky-500/10 p-3 mb-4">
                  <ShieldCheck className="h-6 w-6 text-sky-300" />
                </div>
                <h2 className="text-xl font-semibold text-white">
                  Brand Protection is an upgraded feature
                </h2>
                <p className="mt-2 text-sm text-neutral-400 leading-relaxed">
                  Look-alike domain monitoring, takedown management, and
                  registrar abuse workflows are available on the{" "}
                  <span className="text-sky-200 font-medium">Enterprise</span>{" "}
                  and{" "}
                  <span className="text-sky-200 font-medium">
                    Threat Intelligence
                  </span>{" "}
                  plans.
                </p>
                {brandAccess.licenseType && (
                  <p className="mt-2 text-xs text-neutral-500">
                    Current plan:{" "}
                    <span className="font-mono text-neutral-300 capitalize">
                      {String(brandAccess.licenseType).replace(/_/g, " ")}
                    </span>
                  </p>
                )}
                <div className="mt-6 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/upgrades")}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-sky-600/40 bg-sky-500/15 px-4 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 transition-colors"
                  >
                    View upgrade options
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="rounded-lg border border-sky-900/40 px-4 py-2 text-sm text-neutral-200 hover:text-white hover:bg-white/[0.04] transition-colors"
                  >
                    Back to dashboard
                  </button>
                </div>
              </div>
            </div>
          )}
          {!brandGateActive && <>
          {/* Event-type tabs moved to the strip below the header (matches
              Threat Feeds). See `Sub-nav strip` block above. */}

          {/* Look-alikes sub-tabs — render above the filter bar so users
              pick the view first, then filter within it. Brand-protection
              mode only. */}
          {isLookalikeTab && (
            <div className="mb-3 max-w-8xl border-b border-sky-900/40">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <nav className="flex items-center gap-8 flex-wrap" role="tablist" aria-label="Look-alike views">
                  {[
                    { key: "all", label: "All Domains" },
                    { key: "takedown", label: "Takedown Requests" },
                  ].map((opt) => {
                    const active = lookalikeSubTab === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => setLookalikeSubTab(opt.key)}
                        className={`py-2 text-sm font-medium transition whitespace-nowrap -mb-px border-b-2 ${
                          active
                            ? "text-white border-sky-500"
                            : "text-neutral-400 hover:text-white border-transparent"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </nav>

                <div className="flex items-center gap-2 pb-1.5">
                  {lookalikeSubTab === "all" && (
                    <button
                      type="button"
                      onClick={openExportModal}
                      className="inline-flex items-center gap-1.5 rounded-md border border-sky-900/40 px-3 py-1.5 text-xs text-neutral-200 hover:text-white hover:bg-white/[0.04] transition-colors"
                      title="Configure and export look-alikes as CSV"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Export CSV
                    </button>
                  )}

                  {lookalikeSubTab === "takedown" && (
                    <>
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                          pageTakedownCredits === 0
                            ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                            : "border-sky-500/30 bg-sky-500/10 text-sky-200"
                        }`}
                        title="Available takedown credits"
                      >
                        Available Credits:
                        <span className="font-semibold tabular-nums">
                          {pageTakedownCredits ?? "—"}
                        </span>
                      </span>
                      {pageTakedownCredits === 0 && (
                        <button
                          type="button"
                          onClick={() => navigate("/upgrades?tab=addons")}
                          className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-100 hover:bg-amber-500/20 transition-colors"
                          title="Top up takedown credits"
                        >
                          Top up
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (pageTakedownCredits === 0) {
                            setOutOfCreditsOpen(true);
                          } else {
                            setManualTakedownOpen(true);
                          }
                        }}
                        className="inline-flex items-center gap-1.5 rounded-md border border-sky-600/40 bg-sky-500/10 px-3 py-1.5 text-xs text-sky-100 hover:bg-sky-500/20 transition-colors"
                        title="File an ad hoc takedown for a domain you found yourself"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Ad Hoc Takedown
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="max-w-8xl relative z-[60] mt-4">
            {isLookalikeTab && lookalikeSubTab === "takedown" ? (
              /* Takedown sub-tab: only Status filter is meaningful; everything
                 else (Identifier / Range / etc.) doesn't apply because we
                 filter the already-loaded list client-side. */
              <FilterBar>
                <FilterSegment
                  label="Status"
                  value={takedownStatus}
                  onChange={setTakedownStatus}
                  options={[
                    { key: "all", label: "All" },
                    { key: "pending", label: "Pending" },
                    { key: "submitted", label: "Submitted" },
                    { key: "info needed", label: "Info Needed" },
                    { key: "completed", label: "Completed" },
                    { key: "rejected", label: "Rejected" },
                    { key: "cancelled", label: "Cancelled" },
                  ]}
                />
                <FilterReset
                  onClick={() => setTakedownStatus("all")}
                  className="ml-auto"
                />
              </FilterBar>
            ) : (
            <>
            <FilterBar>
              {/* Identifier — long list, opens a menu */}
              <FilterMenu
                label="Identifier"
                value={selectedWatchlist}
                placeholder={
                  wlLoading
                    ? "Loading…"
                    : wlError
                    ? "Failed"
                    : "All"
                }
                width="w-80"
              >
                {(close) => (
                  <>
                    <FilterMenuItem
                      active={!selectedWatchlist}
                      onClick={() => {
                        setSelectedWatchlist(null);
                        close();
                      }}
                    >
                      All Identifiers
                    </FilterMenuItem>
                    {(() => {
                      const list = watchlistEntries.length
                        ? watchlistEntries
                        : watchlistQueries.map((entry) => ({ entry, entry_type: null }));
                      const expectedType =
                        currentTab === "intel"
                          ? "domain"
                          : currentTab === "darkweb_mention"
                          ? "keyword"
                          : null;
                      return list.map(({ entry, entry_type }) => {
                        const mismatched =
                          expectedType && entry_type && entry_type !== expectedType;
                        const available = !mismatched;
                        return (
                          <FilterMenuItem
                            key={entry}
                            disabled={!available}
                            active={selectedWatchlist === entry}
                            onClick={() => {
                              if (!available) return;
                              setSelectedWatchlist(entry);
                              close();
                            }}
                            title={
                              available
                                ? entry
                                : `${entry} — not applicable for ${
                                    currentTab === "intel" ? "Look-alikes" : "Mentions"
                                  }`
                            }
                          >
                            <span className="truncate min-w-0">{entry}</span>
                            {entry_type && (
                              <span
                                className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-medium ring-1 ${
                                  available
                                    ? "bg-sky-500/10 text-sky-300 ring-sky-500/30"
                                    : "bg-white/[0.04] text-neutral-500 ring-white/10"
                                }`}
                              >
                                {entry_type}
                              </span>
                            )}
                          </FilterMenuItem>
                        );
                      });
                    })()}
                  </>
                )}
              </FilterMenu>

              {/* Data Type — N/A for Look-alike domains */}
              {!isLookalikeTab && (
                <>
                  <FilterDivider />
                  <FilterSegment
                    label="Data"
                    value={dataType}
                    onChange={setDataType}
                    options={[
                      { key: "all", label: "All" },
                      { key: "corporate", label: "Corporate" },
                      { key: "consumer", label: "Consumer" },
                    ]}
                  />
                </>
              )}

              {/* Event Type — full picker shown only on the All tab */}
              {currentTab === "all" && (
                <>
                  <FilterDivider />
                  <FilterMenu
                    label="Event"
                    value={
                      breachType === "all"
                        ? null
                        : {
                            leaks: "Leaks",
                            stealer: "Stealer",
                            combolist: "Combolist",
                            darkweb_mention: "Mentions",
                            exposed_secret: "Exposed Secrets",
                            intel: "Look-alikes",
                          }[breachType] || breachType
                    }
                    placeholder="All"
                    width="w-52"
                  >
                    {(close) =>
                      [
                        { key: "all", label: "All" },
                        { key: "leaks", label: "Leaks" },
                        { key: "stealer", label: "Stealer" },
                        { key: "combolist", label: "Combolist" },
                        { key: "darkweb_mention", label: "Mentions" },
                        { key: "exposed_secret", label: "Exposed Secrets" },
                        { key: "intel", label: "Look-alikes" },
                      ].map((opt) => (
                        <FilterMenuItem
                          key={opt.key}
                          active={breachType === opt.key}
                          onClick={() => {
                            setBreachType(opt.key);
                            close();
                          }}
                        >
                          {opt.label}
                        </FilterMenuItem>
                      ))
                    }
                  </FilterMenu>
                </>
              )}

              {/* Leaks subtype — only when on Leaks tab */}
              {currentTab === "leaks" && (
                <>
                  <FilterDivider />
                  <FilterSegment
                    label="Leaks"
                    value={breachType}
                    onChange={setBreachType}
                    options={[
                      { key: "leaks", label: "All" },
                      { key: "stealer", label: "Stealer" },
                      { key: "combolist", label: "Combolist" },
                    ]}
                  />
                </>
              )}

              <FilterDivider />
              <FilterSegment
                label="Status"
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { key: "all", label: "All" },
                  { key: "open", label: "Open" },
                  { key: "resolved", label: "Resolved" },
                ]}
              />

              <FilterDivider />
              <FilterSegment
                label="Range"
                value={dateQuick}
                onChange={setDateQuick}
                options={[
                  { key: "7d", label: "7d" },
                  { key: "30d", label: "30d" },
                  { key: "90d", label: "90d" },
                  { key: "All", label: "All" },
                ]}
              />

              <FilterReset onClick={resetListFilters} className="ml-auto" />
            </FilterBar>

            <ActiveFilterRow
              chips={[
                selectedWatchlist && {
                  label: "Identifier",
                  value: selectedWatchlist,
                  onClear: () => setSelectedWatchlist(null),
                },
              ]}
            />
            </>
            )}
          </div>


          {/* Look-alike tab gets its own table view (no chart card) */}
          {isLookalikeTab ? (() => {
            const isAll = lookalikeSubTab === "all";
            const isTakedown = lookalikeSubTab === "takedown";
            const displayedRows = lookalikes;
            const displayedLoading = isAll ? lookalikesLoading : false;
            const displayedError = isAll ? lookalikesError : null;
            const emptyMessage =
              "No entries found for given filters.";
            // Client-side status filter for takedown requests — backend
            // returns the full list and we narrow it here based on the Status
            // pill in the toolbar.
            const filteredTakedownRequests =
              takedownStatus === "all"
                ? takedownRequests
                : takedownRequests.filter(
                    (r) => String(r.status || "").toLowerCase() === takedownStatus
                  );
            return (
            <div className="max-w-8xl mt-4">
              <div className={`rounded-md ${frosted} overflow-hidden`}>
                {isTakedown ? (
                  <TakedownRequestsTable
                    rows={filteredTakedownRequests}
                    loading={takedownRequestsLoading}
                    error={takedownRequestsError}
                    summary={takedownRequestsSummary}
                    onOpenDetail={(row) => setSelectedTakedown(row)}
                  />
                ) : (
                  <>
                {displayedLoading && (
                  <div className="p-8 text-neutral-300">Loading…</div>
                )}
                {displayedError && !displayedLoading && (
                  <div className="p-8 text-red-300">{displayedError}</div>
                )}
                {!displayedLoading && !displayedError && displayedRows.length === 0 && (
                  (() => {
                    const hasNoWatchlistEntries = watchlistEntries.length === 0;
                    const hasFiltersApplied =
                      Boolean(selectedWatchlist) ||
                      statusFilter !== "all" ||
                      dateQuick !== "All";
                    return (
                      <div className="px-8 py-14 flex flex-col items-center text-center max-w-xl mx-auto">
                        <div className="rounded-full border border-sky-500/30 bg-sky-500/10 p-3 mb-4">
                          <Eye className="h-6 w-6 text-sky-300" />
                        </div>
                        <h3 className="text-lg font-semibold text-white">
                          {hasNoWatchlistEntries
                            ? "No watchlist entries yet"
                            : hasFiltersApplied
                            ? "No look-alikes match your filters"
                            : "No look-alikes detected yet"}
                        </h3>
                        <p className="mt-2 text-sm text-neutral-400 leading-relaxed">
                          {hasNoWatchlistEntries
                            ? "Brand Protection scans for impersonating domains against your watchlist entries. Add a domain on the Watchlists page and enable Detect Lookalikes to start receiving detections."
                            : hasFiltersApplied
                            ? "Try widening the date range, clearing the identifier filter, or switching the status filter back to All."
                            : "We're scanning your watchlist domains for look-alikes — detections will appear here as they're picked up. If you haven't enabled Detect Lookalikes on a watchlist entry yet, do that on the Watchlists page."}
                        </p>
                        <div className="mt-5 flex items-center gap-2">
                          {hasFiltersApplied && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedWatchlist(null);
                                setStatusFilter("all");
                                setDateQuick("All");
                              }}
                              className="rounded-lg border border-sky-900/40 px-3 py-1.5 text-xs text-neutral-200 hover:text-white hover:bg-white/[0.04] transition-colors"
                            >
                              Reset filters
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => navigate("/watchlists")}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-sky-600/40 bg-sky-500/15 px-3 py-1.5 text-xs font-semibold text-sky-100 hover:bg-sky-500/25 transition-colors"
                          >
                            {hasNoWatchlistEntries ? "Set up Watchlists" : "Manage Watchlists"}
                            <ArrowRight className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })()
                )}
                {!displayedLoading && !displayedError && displayedRows.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-white/[0.03] text-neutral-300">
                        <tr className="border-b border-sky-900/40">
                          <th className="px-4 py-3 text-left font-semibold">Impersonates</th>
                          <th className="px-4 py-3 text-left font-semibold">Detected Domain</th>
                          <th className="px-4 py-3 text-left font-semibold">Takedown</th>
                          <th className="px-4 py-3 text-left font-semibold w-16">Screenshot</th>
                          <th className="px-4 py-3 text-left font-semibold">HTTP</th>
                          <th className="px-4 py-3 text-left font-semibold">MX</th>
                          <th className="px-4 py-3 text-left font-semibold">Registrar</th>
                          <th className="px-4 py-3 text-left font-semibold">Registered</th>
                          <th className="px-4 py-3 text-left font-semibold">Detected</th>
                          <th className="px-4 py-3 text-right font-semibold w-20"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-800">
                        {lookalikes.map((row) => {
                          const status = String(row?.http_status || "").toLowerCase();
                          const code = Number(row?.http_status_code || 0);
                          let statusTone;
                          if (status === "live") {
                            statusTone = "bg-emerald-500/15 text-emerald-200 border-emerald-500/30";
                          } else if (status === "parked" || status === "redirect") {
                            statusTone = "bg-amber-500/15 text-amber-200 border-amber-500/30";
                          } else if (status === "server_error" || (code >= 500 && code < 600)) {
                            statusTone = "bg-rose-500/15 text-rose-200 border-rose-500/30";
                          } else if (status === "client_error" || (code >= 400 && code < 500)) {
                            statusTone = "bg-orange-500/15 text-orange-200 border-orange-500/30";
                          } else {
                            statusTone = "bg-neutral-700/30 text-neutral-300 border-neutral-700";
                          }
                          return (
                            <tr
                              key={row.id}
                              className="group hover:bg-white/[0.03] transition-colors"
                            >
                              <td className="px-4 py-3 align-middle text-sky-200 text-xs">
                                <span className="inline-flex items-center gap-1">
                                  <span>{row.original_entry || "—"}</span>
                                  <CopyButton value={row.original_entry} />
                                </span>
                              </td>
                              <td className="px-4 py-3 align-middle">
                                <div className="flex items-center gap-1">
                                  <span className="font-medium text-white">
                                    {row.detected_domain}
                                  </span>
                                  <CopyButton value={row.detected_domain} />
                                </div>
                                {row.page_title && (
                                  <div className="mt-0.5 text-xs text-neutral-400 truncate max-w-[280px]" title={row.page_title}>
                                    {row.page_title}
                                  </div>
                                )}
                                {row.primary_ip && (
                                  <div className="mt-0.5 text-[11px] text-neutral-500 tabular-nums">
                                    {row.primary_ip}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 align-middle">
                                {(() => {
                                  if (!row.is_takedown_requested) {
                                    return <span className="text-neutral-500">—</span>;
                                  }
                                  const s = String(row.latest_takedown_status || "").toLowerCase();
                                  let tone;
                                  if (s === "completed") tone = "bg-emerald-500/15 text-emerald-200 border-emerald-500/30";
                                  else if (s === "submitted") tone = "bg-sky-500/15 text-sky-200 border-sky-500/30";
                                  else if (s === "pending") tone = "bg-amber-500/15 text-amber-200 border-amber-500/30";
                                  else if (s === "rejected") tone = "bg-rose-500/15 text-rose-200 border-rose-500/30";
                                  else if (s === "info needed" || s === "info_needed") tone = "bg-indigo-500/15 text-indigo-200 border-indigo-500/30";
                                  else if (s === "cancelled" || s === "canceled") tone = "bg-slate-500/15 text-slate-300 border-slate-500/30";
                                  else tone = "bg-neutral-700/30 text-neutral-300 border-neutral-700";
                                  const archived = !row.is_takedown_active;
                                  return (
                                    <span
                                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs capitalize ${tone} ${archived ? "opacity-50" : ""}`}
                                      title={archived ? "Superseded / archived takedown request" : undefined}
                                    >
                                      {s || "filed"}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-3 align-middle">
                                {row.screenshot_url ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setPreviewImage({
                                        url: row.screenshot_url,
                                        domain: row.detected_domain,
                                      })
                                    }
                                    title="Preview screenshot"
                                    className="block"
                                  >
                                    <img
                                      src={row.screenshot_url}
                                      alt=""
                                      className="h-10 w-14 object-cover rounded ring-1 ring-white/10 hover:ring-sky-400/60 transition-shadow cursor-zoom-in"
                                      loading="lazy"
                                    />
                                  </button>
                                ) : (
                                  <div className="h-10 w-14 rounded ring-1 ring-white/10 bg-white/[0.03] flex items-center justify-center text-[10px] text-neutral-500">
                                    n/a
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 align-middle">
                                <span
                                  className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs ${statusTone}`}
                                >
                                  <span className="tabular-nums">
                                    {row.http_status_code || "—"}
                                  </span>
                                  <span className="capitalize">
                                    {(row.http_status || "unknown").replace(/_/g, " ")}
                                  </span>
                                </span>
                              </td>
                              <td className="px-4 py-3 align-middle">
                                {row.has_mx ? (
                                  <span className="inline-flex items-center gap-1.5 text-emerald-300">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                    {row.mx_count || 0}
                                  </span>
                                ) : (
                                  <span className="text-neutral-500">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 align-middle text-neutral-200 max-w-[200px] truncate" title={row.registrar || ""}>
                                {row.registrar || "—"}
                              </td>
                              <td className="px-4 py-3 align-middle text-neutral-300 tabular-nums">
                                {fmtISO(row.created_date)}
                              </td>
                              <td className="px-4 py-3 align-middle text-neutral-300 tabular-nums">
                                {row.detected_at
                                  ? row.detected_at.replace("T", " ").slice(0, 16)
                                  : "—"}
                              </td>
                              <td className="px-4 py-3 align-middle text-right">
                                <button
                                  type="button"
                                  onClick={() => openLookalikeDetail(row)}
                                  className="rounded-lg border border-sky-600/40 bg-sky-500/10 px-3 py-1.5 text-xs text-sky-200 hover:bg-sky-500/20 transition-colors"
                                >
                                  Details
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {isAll && lookalikesHasMore && !lookalikesLoading && !lookalikesError && displayedRows.length > 0 && (
                  <div className="px-4 py-4 border-t border-sky-900/40 flex justify-center">
                    <button
                      type="button"
                      onClick={() => fetchLookalikes({ append: true })}
                      disabled={lookalikesLoadingMoreRef.current}
                      className="rounded-lg border border-sky-600/40 bg-sky-500/10 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-50 transition-colors"
                    >
                      {lookalikesLoadingMoreRef.current ? "Loading…" : "Load more"}
                    </button>
                  </div>
                )}
                  </>
                )}
              </div>
            </div>
            );
          })() : (
          /* Two-column layout */
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.15fr] lg:items-start max-w-8xl">
            {/* LEFT: list */}
            <div className="space-y-4 max-h-[700px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-sky-700 scrollbar-track-transparent relative z-[10]">
              {loadingList && (
                <div className={`rounded-md ${frosted} p-8`}>Loading…</div>
              )}
              {error && !loadingList && (
                <div className={`rounded-md ${frosted} p-8 text-red-300`}>
                  {error}
                </div>
              )}
              {!loadingList && !error && filtered.length === 0 ? (
                <div className={`rounded-md ${frosted} p-8`}>
                  <div className="text-lg font-medium">No new events triggered yet.</div>
                  <div className="mt-1 text-sm text-neutral-400">
                    Visit this page later or try Global Search.
                  </div>
                </div>
              ) : (
                filtered.map((ev) => (
                  <button
                    key={ev.id}
                    onClick={() => openEvent(ev)}
                    className={`group text-left w-full rounded-md ${frosted} p-5 transition hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-sky-500/40`}
                    aria-label={`Open details for event ${ev.title}`}
                  >
                    {/* top row */}
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-neutral-400">
                        {fmtISO(ev.created_at_iso)}
                      </div>
                    </div>

                    {/* title */}
                    <div className="mt-2 text-[18px] md:text-[20px] font-semibold leading-snug">
                      {ev.title}
                    </div>

                    {/* meta row */}
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-neutral-300">
                      <span>
                        {(String(ev?.breach_type || "").toLowerCase() !== "lookalike" &&
                          String(ev?.breach_type || "").toLowerCase() !== "mention" &&
                          String(ev?.breach_type || "").toLowerCase() !== "git_leak"
                        
                        ) && 
                          
                          (
                          <span>
                            {((ev?.credentials_count ?? 0) || 0).toLocaleString()} credentials
                          </span>
                        )}
                      </span>

                      {ev.domain && (
                        <span className="rounded-md border border-sky-900/20 bg-white/5 px-2 py-0.5 text-neutral-200">
                          {ev.domain}
                        </span>
                      )}

                      {ev.email && (
                        <span className="rounded-md border border-sky-900/20 bg-white/5 px-2 py-0.5 text-sky-200">
                          {ev.email}
                        </span>
                      )}
                    </div>

                    {/* optional log date range */}
                    {(ev.min_log_date || ev.max_log_date) && (
                      <div className="mt-1 text-xs text-neutral-400">
                        Log window:{" "}
                        <span className="text-neutral-300">{fmtISO(ev.min_log_date)}</span>
                        {" — "}
                        <span className="text-neutral-300">{fmtISO(ev.max_log_date)}</span>
                      </div>
                    )}

                    {/* tags */}
                    {ev.tags?.length ? (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {ev.tags.map((t, i) => (
                          <span
                            key={`${t}-${i}`}
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${tagClass(
                              String(t).toLowerCase()
                            )}`}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </button>
                ))
              )}

              {/* sentinel for infinite scroll */}
              <div ref={loadMoreRef} className="h-8" />
            </div>

            {/* RIGHT: chart card */}
            <div className={`rounded-md ${frosted} p-5 flex flex-col`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-2xl font-semibold">Events Data</div>
                {/* Bucket selector — scoped to this chart so it lives in the card */}
                <div
                  className="inline-flex rounded-lg border border-sky-900/30 overflow-hidden text-xs"
                  role="tablist"
                  aria-label="Bucket size"
                >
                  {[
                    { key: "day", label: "Day" },
                    { key: "week", label: "Week" },
                    { key: "month", label: "Month" },
                  ].map((opt) => {
                    const active = perBucket === opt.key;
                    return (
                      <button
                        key={opt.key}
                        role="tab"
                        aria-selected={active}
                        onClick={() => setPerBucket(opt.key)}
                        className={`px-3 py-1.5 transition-colors ${
                          active
                            ? "bg-sky-500/15 text-white"
                            : "text-neutral-400 hover:text-white hover:bg-white/[0.04]"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4">
                <div className="text-lg font-semibold">Events</div>
                <div className="h-[2px] w-12 rounded bg-white/20 mt-1" />
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-neutral-300">
                <span>
                  Range: <strong>{dateQuick.toUpperCase()}</strong>
                </span>
                <span>
                  Identifier: <strong>{selectedWatchlist ?? "All"}</strong>
                </span>
                {loadingChart && <span className="opacity-70">Updating…</span>}
              </div>

              <div className="mt-3 h-[360px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                    <XAxis
                      dataKey="label"
                      stroke="#94a3b8"
                      tickLine={false}
                      axisLine={{ stroke: "#334155" }}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      tickLine={false}
                      axisLine={{ stroke: "#334155" }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(56,189,248,0.10)" }}
                      content={({ active, payload, label }) =>
                        active && payload?.length ? (
                          <div className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs text-neutral-200 shadow-xl">
                            <div className="mb-1 text-neutral-400">{label}</div>
                            <div className="font-semibold text-white">{payload[0].value}</div>
                          </div>
                        ) : null
                      }
                    />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          )}
          </>}
        </main>
      </div>

      <ExportConfigModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onExport={handleExportConfigured}
        watchlistOptions={watchlistQueries} // array of strings
        initialRange={dateQuick}
      />

      {/* Lookalike detail sidebar */}
      {lookalikeKvInline && (
        <LookalikeDetailSidebar
          row={lookalikeKvInline}
          full={lookalikeDetailFull}
          loading={lookalikeDetailLoading}
          error={lookalikeDetailError}
          onClose={closeLookalikeDetail}
          onPreviewImage={setPreviewImage}
          onOutOfCredits={() => setOutOfCreditsOpen(true)}
        />
      )}

      {/* Lookalike screenshot lightbox */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          onClick={() => setPreviewImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Screenshot preview"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setPreviewImage(null);
            }}
            className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 text-white p-2 transition-colors"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>
          <div
            className="relative max-w-[95vw] max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {previewImage.domain && (
              <div className="mb-3 px-3 py-1.5 rounded-full bg-white/10 text-white text-sm font-medium">
                {previewImage.domain}
              </div>
            )}
            <img
              src={previewImage.url}
              alt={previewImage.domain || "Screenshot"}
              className="max-w-full max-h-[80vh] object-contain rounded-lg ring-1 ring-white/10 shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Export CSV modal */}
      {exportModalOpen && (
        <div
          className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeExportModal}
          role="dialog"
          aria-modal="true"
          aria-label="Export look-alikes as CSV"
        >
          <div
            className={`w-full max-w-lg rounded-md ${frosted} p-5`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-white">Export CSV</h2>
                <p className="mt-1 text-xs text-neutral-400">
                  Pick which look-alikes to include. The CSV mirrors the
                  visible table columns.
                </p>
              </div>
              <button
                type="button"
                onClick={closeExportModal}
                disabled={exportingCsv}
                className="text-neutral-400 hover:text-white disabled:opacity-40"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 flex flex-col gap-4">
              {/* Identifiers */}
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-neutral-300">
                  Identifiers
                </span>
                <div className="flex items-center gap-2">
                  {[
                    { key: "all", label: "All identifiers" },
                    { key: "selected", label: "Selected" },
                  ].map((opt) => {
                    const active = exportScope === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setExportScope(opt.key)}
                        disabled={exportingCsv}
                        className={`rounded-md px-3 py-1.5 text-xs transition-colors ${
                          active
                            ? "bg-sky-500/15 text-white ring-1 ring-sky-500/40"
                            : "border border-sky-900/40 text-neutral-300 hover:text-white hover:bg-white/[0.04]"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                  {exportScope === "selected" && (
                    <span className="ml-auto text-[11px] text-neutral-400 tabular-nums">
                      {exportSelectedEntries.length} selected
                    </span>
                  )}
                </div>
                {exportScope === "selected" && (
                  <div className="rounded-lg border border-sky-900/40 bg-[#06101e]/60 max-h-40 overflow-y-auto divide-y divide-neutral-800/60">
                    {watchlistEntries.length === 0 ? (
                      <div className="px-3 py-4 text-xs text-neutral-400">
                        No identifiers in your watchlist.
                      </div>
                    ) : (
                      watchlistEntries.map((e) => (
                        <label
                          key={e.entry}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-200 hover:bg-white/[0.03] cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={exportSelectedEntries.includes(e.entry)}
                            onChange={() => toggleExportEntry(e.entry)}
                            disabled={exportingCsv}
                            className="accent-sky-500"
                          />
                          <span className="truncate">{e.entry}</span>
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Date range */}
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-neutral-300">
                  Date range
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { key: "all", label: "All time" },
                    { key: "7d", label: "Last 7d" },
                    { key: "30d", label: "Last 30d" },
                    { key: "90d", label: "Last 90d" },
                  ].map((opt) => {
                    const active = exportDateMode === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setExportDateMode(opt.key)}
                        disabled={exportingCsv}
                        className={`rounded-md px-3 py-1.5 text-xs transition-colors ${
                          active
                            ? "bg-sky-500/15 text-white ring-1 ring-sky-500/40"
                            : "border border-sky-900/40 text-neutral-300 hover:text-white hover:bg-white/[0.04]"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Status */}
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-neutral-300">Status</span>
                <div className="flex items-center gap-2">
                  {[
                    { key: "all", label: "All" },
                    { key: "open", label: "Open" },
                    { key: "resolved", label: "Resolved" },
                    { key: "unresolved", label: "Unresolved" },
                  ].map((opt) => {
                    const active = exportStatus === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setExportStatus(opt.key)}
                        disabled={exportingCsv}
                        className={`rounded-md px-3 py-1.5 text-xs transition-colors ${
                          active
                            ? "bg-sky-500/15 text-white ring-1 ring-sky-500/40"
                            : "border border-sky-900/40 text-neutral-300 hover:text-white hover:bg-white/[0.04]"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeExportModal}
                disabled={exportingCsv}
                className="rounded-lg border border-sky-900/40 px-3 py-1.5 text-xs text-neutral-200 hover:text-white hover:bg-white/[0.04] disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={
                  exportingCsv ||
                  (exportScope === "selected" && exportSelectedEntries.length === 0)
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-sky-600/40 bg-sky-500/15 px-3 py-1.5 text-xs text-white hover:bg-sky-500/25 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Download className="h-3.5 w-3.5" />
                {exportingCsv ? "Exporting…" : "Export"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Out-of-credits interrupt modal */}
      {outOfCreditsOpen && (
        <div
          className="fixed inset-0 z-[96] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOutOfCreditsOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Out of takedown credits"
        >
          <div
            className={`w-full max-w-md rounded-md ${frosted} p-6`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full border border-amber-500/40 bg-amber-500/10 p-2 shrink-0">
                <Gavel className="h-5 w-5 text-amber-300" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-white">
                  Out of takedown credits
                </h2>
                <p className="mt-1 text-sm text-neutral-300 leading-snug">
                  You've used all your managed takedown credits. Top up the
                  Managed Takedowns add-on to file more requests.
                </p>
                <p className="mt-2 text-xs text-neutral-500">
                  Credits are only consumed on actionable cases — rejections by
                  non-ICANN-compliant registrars are refunded.
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOutOfCreditsOpen(false)}
                className="rounded-lg border border-sky-900/40 px-3 py-1.5 text-xs text-neutral-200 hover:text-white hover:bg-white/[0.04]"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={() => {
                  setOutOfCreditsOpen(false);
                  navigate("/upgrades?tab=addons");
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/15 px-3 py-1.5 text-xs font-semibold text-amber-100 hover:bg-amber-500/25"
              >
                <Plus className="h-3.5 w-3.5" />
                Top up Credits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ad Hoc Takedown modal */}
      {manualTakedownOpen && (
        <div
          className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeManualTakedown}
          role="dialog"
          aria-modal="true"
          aria-label="Ad hoc takedown request"
        >
          <div
            className={`w-full max-w-2xl rounded-md ${frosted} p-6`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-white">Ad Hoc Takedown Request</h2>
                <p className="mt-1 text-xs text-neutral-400">
                  File a takedown for a domain you sourced outside our
                  detection list. Uses one takedown credit on submission.
                </p>
              </div>
              <button
                type="button"
                onClick={closeManualTakedown}
                disabled={manualTakedownSubmitting}
                className="text-neutral-400 hover:text-white disabled:opacity-40"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 flex flex-col gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-neutral-300">
                  Domain to take down <span className="text-rose-400">*</span>
                </span>
                <input
                  type="text"
                  value={manualTakedownDomain}
                  onChange={(e) => setManualTakedownDomain(e.target.value)}
                  placeholder="evil-lookalike.example"
                  className="w-full rounded-lg border border-sky-900/40 bg-[#06101e]/80 px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                  autoFocus
                  disabled={manualTakedownSubmitting}
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-neutral-300">
                  Impersonates (your domain)
                </span>
                <input
                  type="text"
                  list="manual-takedown-entries"
                  value={manualTakedownEntry}
                  onChange={(e) => setManualTakedownEntry(e.target.value)}
                  placeholder="acme.com"
                  className="w-full rounded-lg border border-sky-900/40 bg-[#06101e]/80 px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                  disabled={manualTakedownSubmitting}
                />
                <datalist id="manual-takedown-entries">
                  {watchlistEntries.map((e) => (
                    <option key={e.entry} value={e.entry} />
                  ))}
                </datalist>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-neutral-300">
                  Notes / evidence <span className="text-rose-400">*</span>
                </span>
                <textarea
                  value={manualTakedownNote}
                  onChange={(e) => setManualTakedownNote(e.target.value)}
                  placeholder="Why this domain should be taken down (phishing form, brand misuse, evidence URL, etc.)"
                  rows={4}
                  required
                  className="w-full rounded-lg border border-sky-900/40 bg-[#06101e]/80 px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 resize-none"
                  disabled={manualTakedownSubmitting}
                />
              </label>
            </div>

            {manualTakedownStatus && (
              <div
                className={`mt-3 rounded-md border px-3 py-2 text-xs ${
                  manualTakedownStatus.type === "success"
                    ? "border-green-500/30 bg-green-500/10 text-green-200"
                    : "border-red-500/30 bg-red-500/10 text-red-200"
                }`}
              >
                {manualTakedownStatus.message}
              </div>
            )}

            <div className="mt-5 flex items-center justify-between gap-2">
              <span className="text-xs text-neutral-400">
                Available credits:{" "}
                <span className="font-semibold text-neutral-200 tabular-nums">
                  {pageTakedownCredits ?? "—"}
                </span>
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeManualTakedown}
                  disabled={manualTakedownSubmitting}
                  className="rounded-lg border border-sky-900/40 px-3 py-1.5 text-xs text-neutral-200 hover:text-white hover:bg-white/[0.04] disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitManualTakedown}
                  disabled={
                    manualTakedownSubmitting ||
                    !manualTakedownDomain.trim() ||
                    !manualTakedownNote.trim() ||
                    (typeof pageTakedownCredits === "number" && pageTakedownCredits <= 0)
                  }
                  className="rounded-lg border border-sky-600/40 bg-sky-500/15 px-3 py-1.5 text-xs text-white hover:bg-sky-500/25 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {manualTakedownSubmitting ? "Filing…" : "File Takedown"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Takedown request detail side panel */}
      {selectedTakedown && (
        <TakedownRequestDetailPanel
          row={selectedTakedown}
          onClose={() => setSelectedTakedown(null)}
        />
      )}

      {/* Sidebars */}
      {panel === "detail" && selectedEvent && (
        <EventDetailSidebar
          open
          onClose={closePanel}
          size="wide"
          eventId={selectedEvent.id}
          jwtToken={getJwtToken()}
          onStatusChange={handleStatusChange}
        />
      )}

      {panel === "mention" && selectedPostId && (
        <IntelDetailSidebar
          open
          onClose={closePanel}
          postId={selectedPostId}
          jwtToken={getJwtToken()}
          apiUrl={INTEL_API}
          source="watchlist_events"
        />
      )}
    </div>
  );
}


/* ------------------- Lookalike Detail Sidebar ------------------- */
function LookalikeDetailSidebar({ row, full, loading, error, onClose, onPreviewImage, onOutOfCredits }) {
  const d = { ...(row || {}), ...(full || {}) };

  // Action buttons (Jira / Slack / status toggle) — mirror DetailSidebarEvents
  // with event_type: "lookalike" added to every payload.
  const [recordStatus, setRecordStatus] = useState(d.status || "open");
  useEffect(() => {
    if (full?.status) setRecordStatus(full.status);
  }, [full]);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushStatus, setPushStatus] = useState(null);
  const [slackPushing, setSlackPushing] = useState(false);
  const [slackPushStatus, setSlackPushStatus] = useState(null);


  const handleToggleStatus = async () => {
    if (!d?.id || statusUpdating) return;
    const newStatus = (recordStatus || "").toLowerCase() === "resolved" ? "open" : "resolved";
    setStatusUpdating(true);
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/watchlist/status_update.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            id: d.id,
            status: newStatus,
            event_type: "lookalike",
          }),
        }
      );
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      if (!res.ok) throw new Error("Failed to update status");
      setRecordStatus(newStatus);
    } catch (e) {
      // best-effort UI; the toggle simply doesn't flip on failure
    } finally {
      setStatusUpdating(false);
    }
  };

  // Takedown request: opens a popover for note + confirms.
  // Initial credits come from `d.takedown_credits` (if the detail response
  // happens to include it) — the dedicated endpoint below is the real source
  // of truth and overwrites this on mount.
  const [takedownCredits, setTakedownCredits] = useState(
    typeof d.takedown_credits === "number" ? d.takedown_credits : 0
  );
  // Existing takedown record (if one was already filed for this domain).
  // Drives the button's "already requested" state — disabled, shows status
  // instead of the new-request form.
  const [existingTakedown, setExistingTakedown] = useState(
    d.takedown_requested && d.takedown ? d.takedown : null
  );
  useEffect(() => {
    if (!full) return;
    if (full.takedown_requested && full.takedown) {
      setExistingTakedown(full.takedown);
    } else if (full.takedown_requested === false) {
      setExistingTakedown(null);
    }
  }, [full]);

  const fetchTakedownCredits = async () => {
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/watchlist/takedown_credits.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          cache: "no-store",
          body: JSON.stringify({}),
        }
      );
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        return;
      }
      const data = await res.json().catch(() => null);
      if (data?.success && typeof data.credits_remaining === "number") {
        setTakedownCredits(data.credits_remaining);
      }
    } catch {
      // best-effort; keep whatever credits we already have
    }
  };

  // Fetch credits when the sidebar opens (component mount).
  useEffect(() => {
    fetchTakedownCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [takedownPanelOpen, setTakedownPanelOpen] = useState(false);
  const [takedownNote, setTakedownNote] = useState("");
  const [takedownSubmitting, setTakedownSubmitting] = useState(false);
  const [takedownStatus, setTakedownStatus] = useState(null);
  const takedownPanelRef = useRef(null);
  useEffect(() => {
    if (full && typeof full.takedown_credits === "number") {
      setTakedownCredits(full.takedown_credits);
    }
  }, [full]);
  useEffect(() => {
    if (!takedownPanelOpen) return;
    const onDoc = (e) => {
      if (
        takedownPanelRef.current &&
        !takedownPanelRef.current.contains(e.target)
      ) {
        setTakedownPanelOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setTakedownPanelOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [takedownPanelOpen]);

  const handleSubmitTakedown = async () => {
    if (!d?.id || takedownSubmitting || takedownCredits <= 0) return;
    setTakedownSubmitting(true);
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/watchlist/request_takedown.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            id: Number(d.id),
            note: takedownNote.trim(),
          }),
        }
      );
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      const raw = await res.text();
      let result = {};
      try {
        result = raw ? JSON.parse(raw) : {};
      } catch (_e) {
        result = {};
      }
      // Trust server-returned credits regardless of success/failure (e.g.
      // a "no credits" error still ships credits_remaining: 0 — we want the
      // pill to reflect that immediately).
      if (typeof result.credits_remaining === "number") {
        setTakedownCredits(result.credits_remaining);
      }
      const ok = res.ok && (result.status === "success" || result.success === true);
      if (!ok) {
        setTakedownStatus({
          type: "error",
          message:
            result?.error ||
            result?.message ||
            `Failed to file takedown request (HTTP ${res.status}).`,
        });
        return;
      }
      // Successful request and no explicit credit count? Decrement locally.
      if (typeof result.credits_remaining !== "number") {
        setTakedownCredits((c) => Math.max(0, c - 1));
      }
      setTakedownStatus({
        type: "success",
        message:
          result.message ||
          `Takedown request filed for ${d.detected_domain || "this domain"}.`,
      });
      // Lock the button into "already requested" state. Use server-provided
      // takedown record when present, otherwise synthesize a placeholder.
      const nowIso = new Date().toISOString().slice(0, 19).replace("T", " ");
      setExistingTakedown(
        result.takedown || {
          id: result.takedown_id || null,
          status: "pending",
          is_active: true,
          requester_email: result.requester_email || null,
          created_at: nowIso,
          status_updated_at: nowIso,
        }
      );
      setTakedownNote("");
      setTakedownPanelOpen(false);
    } catch (err) {
      setTakedownStatus({
        type: "error",
        message: "Unexpected error while filing takedown request.",
      });
    } finally {
      setTakedownSubmitting(false);
      // Pull authoritative credit count from the dedicated endpoint after
      // every attempt — covers cases where the takedown response didn't
      // include credits_remaining or rate-limited mid-flight.
      fetchTakedownCredits();
    }
  };

  const handlePushToJira = async () => {
    if (!d?.id || pushing) return;
    setPushing(true);
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/jira/create_jira_ticket.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            event_id: Number(d.id),
            event_type: "lookalike",
          }),
        }
      );
      const raw = await res.text();
      let result = {};
      try {
        result = raw ? JSON.parse(raw) : {};
      } catch (_e) {
        result = {};
      }
      const ok = res.ok && (result.status === "success" || result.success === true);
      if (!ok) {
        setPushStatus({
          type: "error",
          message:
            result?.error ||
            result?.message ||
            `Failed to create Jira ticket (HTTP ${res.status}).`,
        });
        return;
      }
      const issueKey = result.jira_ticket || result.issueKey;
      const issueUrl = result.ticket_url || result.issueUrl;
      const ticketSuffix = issueKey ? ` (${issueKey})` : "";
      setPushStatus({
        type: "success",
        message: `${result.message || "Jira ticket created successfully"}${ticketSuffix}`,
        issueKey,
        issueUrl,
      });
    } catch (err) {
      setPushStatus({ type: "error", message: "Unexpected error while pushing to Jira." });
    } finally {
      setPushing(false);
    }
  };

  const handlePushToSlack = async () => {
    if (!d?.id || slackPushing) return;
    setSlackPushing(true);
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/slack/send_slack.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            event_id: Number(d.id),
            event_type: "lookalike",
          }),
        }
      );
      const raw = await res.text();
      let result = {};
      try {
        result = raw ? JSON.parse(raw) : {};
      } catch (_e) {
        result = {};
      }
      const ok = res.ok && (result.status === "success" || result.success === true);
      if (!ok) {
        setSlackPushStatus({
          type: "error",
          message:
            result?.error ||
            result?.message ||
            `Failed to send Slack alert (HTTP ${res.status}).`,
        });
        return;
      }
      setSlackPushStatus({
        type: "success",
        message: result.message || "Slack alert sent successfully",
      });
    } catch (err) {
      setSlackPushStatus({
        type: "error",
        message: "Unexpected error while sending Slack alert.",
      });
    } finally {
      setSlackPushing(false);
    }
  };

  const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : v);
  const isLive = String(d?.http_status || "").toLowerCase() === "live";
  const mxCount = Number(d?.mx_count || 0);
  const hasMx = !!d?.has_mx && mxCount > 0;

  const status = String(d?.http_status || "").toLowerCase();
  const code = Number(d?.http_status_code || 0);
  let statusTone;
  if (status === "live") statusTone = "bg-emerald-500/15 text-emerald-200 border-emerald-500/30";
  else if (status === "parked" || status === "redirect") statusTone = "bg-amber-500/15 text-amber-200 border-amber-500/30";
  else if (status === "server_error" || (code >= 500 && code < 600)) statusTone = "bg-rose-500/15 text-rose-200 border-rose-500/30";
  else if (status === "client_error" || (code >= 400 && code < 500)) statusTone = "bg-orange-500/15 text-orange-200 border-orange-500/30";
  else statusTone = "bg-neutral-700/30 text-neutral-300 border-neutral-700";

  const target = d.original_entry || "the protected asset";
  const dom = d.detected_domain || "the registered domain";

  const liveClause = isLive
    ? "resolves to a live endpoint"
    : status === "parked" || status === "redirect"
    ? `is currently ${status}`
    : status === "unreachable"
    ? "is currently unreachable"
    : code >= 400
    ? `returns ${code} ${status.replace(/_/g, " ")}`
    : "has been observed";

  const mxClause = hasMx
    ? ` and is provisioned with ${mxCount} MX record${mxCount === 1 ? "" : "s"}, giving the operator infrastructure to send and receive email under a deceptively similar identity`
    : ", with no MX records configured at the time of detection";

  const ageDays = Number(d?.registration?.age_days);
  const ageClause = Number.isFinite(ageDays)
    ? ageDays <= 30
      ? ` The domain was registered ${ageDays} day${ageDays === 1 ? "" : "s"} ago — a freshly registered domain is a strong indicator of a deliberate, targeted operation.`
      : ageDays <= 365
      ? ` The domain was registered approximately ${Math.round(ageDays / 30)} month${ageDays > 60 ? "s" : ""} ago.`
      : ` The domain was registered approximately ${Math.round(ageDays / 365)} year${ageDays > 730 ? "s" : ""} ago, suggesting it may have been re-purposed rather than newly registered.`
    : "";

  const risk = `A look-alike domain (${dom}) has been registered to closely resemble the legitimate asset ${target}. The domain ${liveClause}${mxClause}.${ageClause} Domains of this kind are commonly used in credential-harvesting and business-email-compromise campaigns. Without intervention, the operator may host branded phishing content, intercept inbound traffic, or weaponize the registration in targeted social-engineering attacks.`;

  const detectedHuman = d.detected_at
    ? String(d.detected_at).replace("T", " ").slice(0, 16)
    : "the time of detection";

  return (
    <div
      className="fixed inset-0 z-[80]"
      role="dialog"
      aria-modal="true"
      aria-label="Look-alike detail"
    >
      <div
        className="absolute inset-0 bg-black/60 transition-opacity"
        onClick={onClose}
      />
      <aside className="absolute right-0 top-0 h-full w-full max-w-3xl border-l border-neutral-800 bg-[#000414] shadow-[0_0_15px_rgba(56,189,248,0.05)] overflow-y-auto">
        {/* Sticky header + banner stack — stays visible while scrolling so
            users see action results without having to scroll back to the top. */}
        <div className="sticky top-0 z-10 bg-[#000414]/95 backdrop-blur">
        {/* Header */}
        <div className="border-b border-neutral-800 px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-wider text-sky-300/80 font-semibold mb-1">
              Look-alike Domain Finding
            </div>
            <div className="text-xl font-semibold text-white truncate font-mono">{dom}</div>
            {d.original_entry && (
              <div className="mt-0.5 text-xs text-neutral-400">
                Targets <span className="text-sky-200 font-mono">{d.original_entry}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleToggleStatus}
              disabled={statusUpdating}
              className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                (recordStatus || "").toLowerCase() === "resolved"
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                  : "border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
              }`}
              title={
                (recordStatus || "").toLowerCase() === "resolved"
                  ? "Mark as Open"
                  : "Mark as Resolved"
              }
            >
              {statusUpdating
                ? "Updating…"
                : (recordStatus || "").toLowerCase() === "resolved"
                ? "Mark as Open"
                : "Mark as Resolved"}
            </button>
            <SidebarSpinBtn
              onClick={handlePushToJira}
              loading={pushing}
              icon={<SiJira className="w-4 h-4" />}
              title="Push to Jira"
            />
            <SidebarSpinBtn
              onClick={handlePushToSlack}
              loading={slackPushing}
              icon={<SlackIcon className="w-4 h-4" />}
              title="Push to Slack"
            />
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-neutral-400 hover:text-white hover:bg-white/5 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Action status banners — sit inside the sticky stack so they're
            always visible regardless of scroll position. */}
        <SidebarStatusBanner status={takedownStatus} onDismiss={() => setTakedownStatus(null)} />
        <SidebarStatusBanner status={pushStatus} onDismiss={() => setPushStatus(null)} />
        <SidebarStatusBanner status={slackPushStatus} onDismiss={() => setSlackPushStatus(null)} />
        </div>

        {/* Content */}
        <div className="px-5 py-5 space-y-3">
          {/* Pentest-style finding */}
          <div className="rounded-md border border-sky-900/40 bg-gradient-to-b from-sky-500/[0.05] to-sky-500/[0.02] p-4">
            <div className="grid grid-cols-2 gap-3 mb-3 pb-3 border-b border-sky-900/30">
              <KvInline label="Asset" mono>{dom}</KvInline>
              <KvInline label="First Detected" mono>{detectedHuman}</KvInline>
            </div>

            <div className="text-[10px] uppercase tracking-wider text-sky-300 font-semibold mb-1.5">Risk</div>
            <p className="text-sm text-neutral-200 leading-relaxed">{risk}</p>

            <div className="text-[10px] uppercase tracking-wider text-sky-300 font-semibold mt-4 mb-1.5">Recommendation</div>
            <ul className="text-sm text-neutral-200 leading-relaxed list-disc pl-5 space-y-1">
              <li>Capture HTML, screenshots, and infrastructure metadata for evidence retention.</li>
              <li>Add <span className="font-mono text-sky-200">{dom}</span> to web proxy and DNS blocklists.</li>
              {hasMx && (
                <li>Configure inbound email gateway rules to quarantine traffic from this domain and alert on SPF/DKIM/DMARC failures.</li>
              )}
              <li>Monitor for follow-on registrations of similar variants targeting <span className="font-mono text-sky-200">{target}</span>.</li>
            </ul>
          </div>

          {/* Primary action: Request Takedown */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1" ref={takedownPanelRef}>
              {existingTakedown ? (
                <>
                  {(() => {
                    const tdStatus = String(existingTakedown.status || "pending").toLowerCase();
                    const statusTone =
                      tdStatus === "completed"
                        ? "bg-emerald-500/20 text-emerald-100 ring-emerald-500/40"
                        : tdStatus === "submitted"
                        ? "bg-sky-500/20 text-sky-100 ring-sky-500/40"
                        : tdStatus === "rejected"
                        ? "bg-rose-500/20 text-rose-100 ring-rose-500/40"
                        : "bg-amber-500/20 text-amber-100 ring-amber-500/40";
                    return (
                      <button
                        type="button"
                        onClick={() => setTakedownPanelOpen((o) => !o)}
                        title="Takedown already requested for this domain"
                        aria-expanded={takedownPanelOpen}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-2.5 text-sm font-semibold text-emerald-100 hover:bg-emerald-500/15 transition-colors"
                      >
                        <Gavel className="h-4 w-4" />
                        <span>Takedown Requested</span>
                        <span
                          className={`rounded-full ring-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider capitalize ${statusTone}`}
                        >
                          {tdStatus.replace(/_/g, " ")}
                        </span>
                      </button>
                    );
                  })()}
                  {takedownPanelOpen && (
                    <div className="absolute left-0 right-0 top-full mt-2 z-50 rounded-md border border-emerald-500/30 bg-[#000414] shadow-2xl text-left">
                      <div className="px-4 pt-3.5">
                        <div className="flex items-center gap-2 mb-1">
                          <Gavel className="h-4 w-4 text-emerald-300 shrink-0" />
                          <h3 className="text-sm font-semibold text-white">
                            Takedown Already Requested
                          </h3>
                        </div>
                        <p className="text-xs text-neutral-400 leading-relaxed">
                          A takedown request has already been filed for{" "}
                          <span className="font-mono text-emerald-200">{dom}</span>.
                          You'll be notified when the registrar / host updates the status.
                        </p>
                      </div>
                      <div className="px-4 py-3 mt-2 border-t border-white/5 grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">
                            Status
                          </div>
                          <div className="text-neutral-200 capitalize">
                            {String(existingTakedown.status || "pending").replace(/_/g, " ")}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">
                            Filed
                          </div>
                          <div className="text-neutral-200 font-mono">
                            {existingTakedown.created_at
                              ? String(existingTakedown.created_at).replace("T", " ").slice(0, 16)
                              : "—"}
                          </div>
                        </div>
                        {existingTakedown.requester_email && (
                          <div className="col-span-2">
                            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">
                              Requested by
                            </div>
                            <div className="text-neutral-200 font-mono break-all">
                              {existingTakedown.requester_email}
                            </div>
                          </div>
                        )}
                        {existingTakedown.status_updated_at &&
                          existingTakedown.status_updated_at !== existingTakedown.created_at && (
                            <div className="col-span-2">
                              <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">
                                Last updated
                              </div>
                              <div className="text-neutral-200 font-mono">
                                {String(existingTakedown.status_updated_at)
                                  .replace("T", " ")
                                  .slice(0, 16)}
                              </div>
                            </div>
                          )}
                      </div>
                      <div className="px-3 py-2.5 border-t border-white/5 flex items-center justify-end">
                        <button
                          type="button"
                          onClick={() => setTakedownPanelOpen(false)}
                          className="rounded-md border border-neutral-700 bg-neutral-900/40 px-2.5 py-1 text-xs text-neutral-200 hover:bg-neutral-900 transition-colors"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (takedownCredits <= 0) {
                        onOutOfCredits?.();
                      } else {
                        setTakedownPanelOpen((o) => !o);
                      }
                    }}
                    title={
                      takedownCredits > 0
                        ? "Request takedown"
                        : "No takedown credits remaining — top up to file"
                    }
                    aria-expanded={takedownPanelOpen}
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors ${
                      takedownCredits > 0
                        ? "border-rose-500/50 bg-rose-500/10 text-rose-100 hover:bg-rose-500/20"
                        : "border-amber-500/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20"
                    }`}
                  >
                    <Gavel className="h-4 w-4" />
                    <span>Request Takedown</span>
                    <span
                      className="rounded-full bg-rose-500/20 ring-1 ring-rose-500/40 px-2 py-0.5 text-[10px] font-bold text-rose-100"
                      title="Takedown credits remaining"
                    >
                      Available Credits: <span className="tabular-nums">{takedownCredits}</span>
                    </span>
                  </button>
                  {takedownPanelOpen && (
                    <div className="absolute left-0 right-0 top-full mt-2 z-50 rounded-md border border-rose-500/40 bg-[#000414] shadow-2xl text-left">
                      <div className="px-4 pt-3.5">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <Gavel className="h-4 w-4 text-rose-300 shrink-0" />
                            <h3 className="text-sm font-semibold text-white">Request Takedown</h3>
                          </div>
                          <span className="rounded-full bg-rose-500/15 ring-1 ring-rose-500/30 text-rose-100 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5">
                            Available Credits: <span className="tabular-nums">{takedownCredits}</span>
                          </span>
                        </div>
                        <p className="text-xs text-neutral-400 leading-relaxed">
                          File a takedown request for{" "}
                          <span className="font-mono text-rose-200">{dom}</span> with the
                          registrar / hosting provider. Each filing consumes one credit.
                        </p>
                      </div>
                      <div className="px-4 py-3 mt-2 border-t border-white/5">
                        <label className="block text-[10px] uppercase tracking-wider text-neutral-500 mb-1">
                          Note (optional)
                        </label>
                        <textarea
                          value={takedownNote}
                          onChange={(e) => setTakedownNote(e.target.value)}
                          placeholder="Add evidence, brand-impersonation context, or urgency notes…"
                          rows={3}
                          maxLength={500}
                          className="w-full rounded-md border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-sm text-neutral-100 placeholder-neutral-500 outline-none focus-visible:ring-1 focus-visible:ring-rose-500/40 resize-none"
                        />
                      </div>
                      <div className="px-3 py-2.5 border-t border-white/5 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setTakedownPanelOpen(false)}
                          className="rounded-md border border-neutral-700 bg-neutral-900/40 px-2.5 py-1 text-xs text-neutral-200 hover:bg-neutral-900 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSubmitTakedown}
                          disabled={takedownSubmitting || takedownCredits <= 0}
                          className="rounded-md border border-rose-500/40 bg-rose-500/15 px-2.5 py-1 text-xs font-semibold text-rose-100 hover:bg-rose-500/25 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                        >
                          {takedownSubmitting ? "Filing…" : "File Request"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {loading && !full && (
            <div className="text-sm text-neutral-400">Loading additional details…</div>
          )}
          {error && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
              Failed to load full detail: {error}
            </div>
          )}

          {/* Screenshot */}
          {d.screenshot_url && (
            <CollapsibleSection title="Screenshot">
              <button
                type="button"
                onClick={() => onPreviewImage?.({ url: d.screenshot_url, domain: d.detected_domain })}
                className="block w-full"
                title="Click to enlarge"
              >
                <img
                  src={d.screenshot_url}
                  alt={d.detected_domain || ""}
                  className="w-full max-h-[280px] object-cover object-top rounded-lg ring-1 ring-white/10 hover:ring-sky-400/60 cursor-zoom-in"
                />
              </button>
            </CollapsibleSection>
          )}

          {/* HTTP — short, horizontal layout */}
          <CollapsibleSection title="HTTP" empty={!d.http_status_code && !d.http_status}>
            <KvGrid
              cols={3}
              items={[
                {
                  label: "Status",
                  value: (
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs ${statusTone}`}>
                      <span className="tabular-nums">{fmt(d.http_status_code)}</span>
                      <span className="capitalize">{(d.http_status || "unknown").replace(/_/g, " ")}</span>
                    </span>
                  ),
                },
                { label: "Primary IP", value: fmt(d.primary_ip), mono: true },
                { label: "Final URL", value: fmt(d.final_url || d.detected_domain), mono: true, span: 1 },
              ]}
            />
          </CollapsibleSection>

          {/* Title */}
          <CollapsibleSection title="Page Title" empty={!d.page_title}>
            <div className="text-sm text-neutral-200 break-words">{fmt(d.page_title)}</div>
          </CollapsibleSection>

          {/* Redirect chain */}
          <CollapsibleSection
            title="Redirect Chain"
            badge={Array.isArray(d.redirect_chain) ? d.redirect_chain.length : 0}
            empty={!Array.isArray(d.redirect_chain) || d.redirect_chain.length === 0}
          >
            {Array.isArray(d.redirect_chain) && d.redirect_chain.length > 0 ? (
              <ol className="space-y-2 text-sm">
                {d.redirect_chain.map((hop, i) => {
                  // Tolerate both string and object hop shapes
                  if (typeof hop === "string") {
                    return (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-neutral-500 tabular-nums text-xs mt-0.5">{i + 1}.</span>
                        <span className="text-neutral-200 font-mono text-xs break-all">{hop}</span>
                      </li>
                    );
                  }
                  const hopCode = Number(hop?.status || 0);
                  const hopTone =
                    hopCode >= 200 && hopCode < 300
                      ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
                      : hopCode >= 300 && hopCode < 400
                      ? "bg-sky-500/15 text-sky-200 border-sky-500/30"
                      : hopCode >= 400 && hopCode < 500
                      ? "bg-orange-500/15 text-orange-200 border-orange-500/30"
                      : hopCode >= 500
                      ? "bg-rose-500/15 text-rose-200 border-rose-500/30"
                      : "bg-neutral-700/30 text-neutral-300 border-neutral-700";
                  return (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-neutral-500 tabular-nums text-xs mt-1 shrink-0">{i + 1}.</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center rounded border px-1.5 py-0 text-[10px] font-bold tabular-nums ${hopTone}`}>
                            {hopCode || "—"}
                          </span>
                          <span className="text-neutral-200 font-mono text-xs break-all">{hop?.url || "—"}</span>
                        </div>
                        {(hop?.location || hop?.server) && (
                          <div className="mt-1 text-[11px] text-neutral-500 space-y-0.5">
                            {hop?.location && (
                              <div>
                                <span className="text-neutral-600">→ </span>
                                <span className="font-mono break-all text-neutral-400">{hop.location}</span>
                              </div>
                            )}
                            {hop?.server && (
                              <div>
                                <span className="text-neutral-600">server: </span>
                                <span className="font-mono">{hop.server}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className="text-sm text-neutral-500">No redirects</div>
            )}
          </CollapsibleSection>

          {/* DNS records — backend returns an object keyed by record type:
              { A: [...], AAAA: [...], TXT: [...], ... } */}
          <CollapsibleSection
            title="DNS Records"
            badge={(() => {
              const dns = d.dns_records;
              if (dns && typeof dns === "object" && !Array.isArray(dns)) {
                return Object.values(dns).reduce(
                  (n, v) => n + (Array.isArray(v) ? v.length : 0),
                  0
                );
              }
              return Array.isArray(dns) ? dns.length : 0;
            })()}
            empty={(() => {
              const dns = d.dns_records;
              if (dns && typeof dns === "object" && !Array.isArray(dns)) {
                return Object.values(dns).every(
                  (v) => !Array.isArray(v) || v.length === 0
                );
              }
              return !Array.isArray(dns) || dns.length === 0;
            })()}
          >
            {(() => {
              const dns = d.dns_records;
              const entries =
                dns && typeof dns === "object" && !Array.isArray(dns)
                  ? Object.entries(dns).filter(
                      ([, vals]) => Array.isArray(vals) && vals.length > 0
                    )
                  : Array.isArray(dns)
                  ? dns.map((r) => [r.type, [r.value]])
                  : [];
              if (entries.length === 0) {
                return <div className="text-sm text-neutral-500">No DNS records returned</div>;
              }
              return (
                <div className="overflow-hidden rounded-lg ring-1 ring-white/10">
                  <table className="w-full text-xs">
                    <thead className="bg-white/[0.03] text-neutral-400">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold w-16">Type</th>
                        <th className="px-3 py-2 text-left font-semibold">Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800">
                      {entries.flatMap(([type, values]) =>
                        values.map((val, i) => (
                          <tr key={`${type}-${i}`}>
                            <td className="px-3 py-2 font-mono text-sky-200 align-top">{type}</td>
                            <td className="px-3 py-2 font-mono text-neutral-200 break-all">{val}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </CollapsibleSection>

          {/* MX records */}
          <CollapsibleSection
            title="MX Records"
            badge={Array.isArray(d.mx_records) ? d.mx_records.length : Number(d.mx_count) || 0}
            empty={!d.has_mx && (!Array.isArray(d.mx_records) || d.mx_records.length === 0)}
          >
            {Array.isArray(d.mx_records) && d.mx_records.length > 0 ? (
              <div className="overflow-hidden rounded-lg ring-1 ring-white/10">
                <table className="w-full text-xs">
                  <thead className="bg-white/[0.03] text-neutral-400">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold w-20">Priority</th>
                      <th className="px-3 py-2 text-left font-semibold">Host</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {d.mx_records.map((r, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2 tabular-nums text-neutral-200">{r.priority ?? "—"}</td>
                        <td className="px-3 py-2 font-mono text-neutral-200 break-all">{r.host}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-neutral-500">
                {hasMx ? `${mxCount} MX record${mxCount === 1 ? "" : "s"} reported` : "No MX records"}
              </div>
            )}
          </CollapsibleSection>

          {/* WHOIS — combines `registration` (curated) + `whois_full` (raw) */}
          <CollapsibleSection
            title="WHOIS"
            empty={!d.registration && !d.whois_full && !d.registrar}
          >
            {(() => {
              const reg = d.registration || {};
              const wf = d.whois_full || {};
              const country = (reg.registrant_country || d.registrant_country || "").replace(
                /REDACTED.*PRIVACY/i,
                "—"
              );
              const ageStr = Number.isFinite(Number(reg.age_days))
                ? `${reg.age_days} day${reg.age_days === 1 ? "" : "s"}`
                : "—";
              const nameServers =
                (Array.isArray(wf.name_servers) && wf.name_servers) ||
                (Array.isArray(d.detected_nameservers) && d.detected_nameservers) ||
                [];
              return (
                <>
                  <KvGrid
                    cols={3}
                    items={[
                      { label: "Registrar", value: fmt(reg.registrar || wf.registrar || d.registrar) },
                      { label: "Registrant", value: fmt(reg.registrant_name || wf.name) },
                      { label: "Country", value: fmt(country) },
                      { label: "Created", value: fmt(reg.created_date), mono: true },
                      { label: "Updated", value: fmt(reg.updated_date), mono: true },
                      { label: "Expires", value: fmt(reg.expires_date), mono: true },
                      { label: "Age", value: ageStr },
                      { label: "DNSSEC", value: fmt(wf.dnssec) },
                      { label: "Email", value: fmt(wf.emails || reg.registrant_email), mono: true },
                    ]}
                  />
                  {nameServers.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">Name Servers</div>
                      <div className="flex flex-wrap gap-1.5">
                        {nameServers.map((ns, i) => (
                          <span key={i} className="rounded bg-white/[0.04] px-2 py-0.5 ring-1 ring-white/10 text-xs font-mono">
                            {ns}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </CollapsibleSection>

          {/* SSL — backend splits into ssl_summary (parsed) + ssl_full (raw / fingerprint) */}
          <CollapsibleSection
            title="SSL Certificate"
            empty={!d.ssl_summary && !d.ssl_full && !d.ssl}
          >
            {(() => {
              const sum = d.ssl_summary || d.ssl || null;
              const full = d.ssl_full || null;
              if (!sum && !full) {
                return <div className="text-sm text-neutral-500">SSL certificate info not available</div>;
              }
              const parseError = !!full?.parse_error;
              return (
                <>
                  {parseError && (
                    <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                      Certificate could not be fully parsed; only fingerprint is reliable.
                    </div>
                  )}
                  <KvGrid
                    cols={2}
                    items={[
                      { label: "Issuer", value: fmt(sum?.issuer) },
                      { label: "Subject", value: fmt(sum?.subject), mono: true },
                      { label: "Valid From", value: fmt(sum?.valid_from), mono: true },
                      { label: "Valid To", value: fmt(sum?.valid_to), mono: true },
                      {
                        label: "Self-signed",
                        value: sum?.self_signed === null || sum?.self_signed === undefined ? "—" : sum.self_signed ? "Yes" : "No",
                      },
                      { label: "SHA-256 fingerprint", value: fmt(full?.sha256_fingerprint), mono: true, span: 2 },
                    ]}
                  />
                  {Array.isArray(sum?.san) && sum.san.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">Subject Alt Names</div>
                      <div className="flex flex-wrap gap-1.5">
                        {sum.san.map((s, i) => (
                          <span key={i} className="rounded bg-white/[0.04] px-2 py-0.5 ring-1 ring-white/10 text-xs font-mono">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </CollapsibleSection>

          {/* Response Headers — useful for stack fingerprinting */}
          <CollapsibleSection
            title="Response Headers"
            badge={d.response_headers ? Object.keys(d.response_headers).length : 0}
            empty={!d.response_headers || Object.keys(d.response_headers).length === 0}
          >
            {d.response_headers && Object.keys(d.response_headers).length > 0 ? (
              <div className="overflow-hidden rounded-lg ring-1 ring-white/10">
                <table className="w-full text-xs">
                  <thead className="bg-white/[0.03] text-neutral-400">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold w-40">Header</th>
                      <th className="px-3 py-2 text-left font-semibold">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {Object.entries(d.response_headers).map(([k, v]) => (
                      <tr key={k}>
                        <td className="px-3 py-2 font-mono text-sky-200 align-top break-all">{k}</td>
                        <td className="px-3 py-2 font-mono text-neutral-200 break-all">
                          {Array.isArray(v) ? v.join(", ") : String(v)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-neutral-500">No response headers captured</div>
            )}
          </CollapsibleSection>

          {/* Detected infrastructure (IPs + name servers observed at scan time) */}
          {((Array.isArray(d.detected_ips) && d.detected_ips.length > 0) ||
            (Array.isArray(d.detected_nameservers) && d.detected_nameservers.length > 0) ||
            d.primary_ip ||
            d.primary_ns) && (
            <CollapsibleSection
              title="Infrastructure"
              badge={
                (Array.isArray(d.detected_ips) ? d.detected_ips.length : 0) +
                (Array.isArray(d.detected_nameservers) ? d.detected_nameservers.length : 0) +
                (d.primary_ip ? 1 : 0) +
                (d.primary_ns ? 1 : 0)
              }
            >
              <div className="space-y-3">
                {(d.primary_ip || (Array.isArray(d.detected_ips) && d.detected_ips.length > 0)) && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">
                      IP Addresses
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {d.primary_ip && (
                        <span className="rounded bg-sky-500/10 px-2 py-0.5 ring-1 ring-sky-500/30 text-xs font-mono text-sky-200">
                          {d.primary_ip} <span className="text-sky-400/70">(primary)</span>
                        </span>
                      )}
                      {(d.detected_ips || [])
                        .filter((ip) => ip !== d.primary_ip)
                        .map((ip, i) => (
                          <span key={i} className="rounded bg-white/[0.04] px-2 py-0.5 ring-1 ring-white/10 text-xs font-mono">
                            {ip}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
                {(d.primary_ns ||
                  (Array.isArray(d.detected_nameservers) && d.detected_nameservers.length > 0)) && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">
                      Name Servers (observed)
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {d.primary_ns && (
                        <span className="rounded bg-sky-500/10 px-2 py-0.5 ring-1 ring-sky-500/30 text-xs font-mono text-sky-200">
                          {d.primary_ns} <span className="text-sky-400/70">(primary)</span>
                        </span>
                      )}
                      {(d.detected_nameservers || [])
                        .filter((ns) => ns !== d.primary_ns)
                        .map((ns, i) => (
                          <span key={i} className="rounded bg-white/[0.04] px-2 py-0.5 ring-1 ring-white/10 text-xs font-mono">
                            {ns}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* Hashes */}
          <CollapsibleSection
            title="Hashes"
            badge={(d.favicon_hash ? 1 : 0) + (d.page_html_hash ? 1 : 0)}
            empty={!d.favicon_hash && !d.page_html_hash}
          >
            <KvGrid
              cols={1}
              items={[
                { label: "Favicon hash", value: fmt(d.favicon_hash), mono: true },
                { label: "Page HTML hash", value: fmt(d.page_html_hash), mono: true },
              ]}
            />
          </CollapsibleSection>

          {/* Detection metadata */}
          <CollapsibleSection title="Detection Metadata">
            <KvGrid
              cols={3}
              items={[
                { label: "First detected", value: detectedHuman, mono: true },
                { label: "Source ID", value: fmt(d.id), mono: true },
                { label: "Original entry", value: fmt(d.original_entry), mono: true },
              ]}
            />
          </CollapsibleSection>
        </div>
      </aside>
    </div>
  );
}

function CollapsibleSection({ title, defaultOpen = false, badge, empty, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const hasBadge = typeof badge === "number" && badge > 0;
  return (
    <section className="rounded-md border border-sky-900/30 bg-white/[0.02] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-white/[0.02] transition-colors text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-neutral-200">
            {title}
          </span>
          {hasBadge && (
            <span className="inline-flex items-center rounded-full bg-sky-500/15 ring-1 ring-sky-500/30 text-sky-300 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums">
              {badge}
            </span>
          )}
          {!hasBadge && empty && (
            <span className="text-[10px] uppercase tracking-wider text-neutral-600">
              no data
            </span>
          )}
          {!hasBadge && !empty && badge === undefined && (
            <span
              className="h-1.5 w-1.5 rounded-full bg-emerald-400/70"
              aria-label="data available"
            />
          )}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-neutral-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-sky-900/20">{children}</div>
      )}
    </section>
  );
}

function KvGrid({ cols = 3, items }) {
  const colClass =
    cols === 1
      ? "grid-cols-1"
      : cols === 2
      ? "grid-cols-1 sm:grid-cols-2"
      : "grid-cols-2 sm:grid-cols-3";
  return (
    <dl className={`grid ${colClass} gap-x-4 gap-y-3`}>
      {items.map((it, i) => (
        <div key={i} className={it.span === 2 ? "col-span-full" : ""}>
          <dt className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">{it.label}</dt>
          <dd className={`text-sm text-neutral-200 break-words ${it.mono ? "font-mono text-xs" : ""}`}>
            {it.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function KvInline({ label, children, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">{label}</div>
      <div className={`text-sm text-neutral-200 break-words ${mono ? "font-mono text-xs" : ""}`}>
        {children}
      </div>
    </div>
  );
}

function CopyButton({ value, className = "" }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const onClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={copied ? "Copied" : `Copy ${value}`}
      aria-label={`Copy ${value}`}
      className={`inline-flex h-5 w-5 items-center justify-center rounded text-neutral-400 hover:text-sky-300 hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 ${className}`}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function TakedownRequestsTable({ rows, loading, error, summary, onOpenDetail }) {
  const fmtTs = (ts) =>
    ts ? String(ts).replace("T", " ").slice(0, 16) : "—";
  const statusTone = (s) => {
    const v = String(s || "").toLowerCase();
    if (v === "completed") return "bg-emerald-500/15 text-emerald-200 border-emerald-500/30";
    if (v === "submitted") return "bg-sky-500/15 text-sky-200 border-sky-500/30";
    if (v === "pending") return "bg-amber-500/15 text-amber-200 border-amber-500/30";
    if (v === "rejected") return "bg-rose-500/15 text-rose-200 border-rose-500/30";
    if (v === "info needed" || v === "info_needed") return "bg-indigo-500/15 text-indigo-200 border-indigo-500/30";
    if (v === "cancelled" || v === "canceled") return "bg-slate-500/15 text-slate-300 border-slate-500/30";
    return "bg-neutral-700/30 text-neutral-300 border-neutral-700";
  };

  const SummaryStrip = () =>
    summary ? (
      <div className="flex flex-wrap items-center gap-2 border-b border-sky-900/40 px-4 py-3 text-xs">
        {[
          { key: "pending", label: "Pending" },
          { key: "submitted", label: "Submitted" },
          { key: "info needed", label: "Info Needed", summaryKey: "info_needed" },
          { key: "completed", label: "Completed" },
          { key: "rejected", label: "Rejected" },
          { key: "cancelled", label: "Cancelled" },
        ].map(({ key, label, summaryKey }) => (
          <span
            key={key}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 ${statusTone(key)}`}
          >
            <span className="capitalize">{label}</span>
            <span className="font-semibold tabular-nums">
              {Number(summary?.[summaryKey ?? key] ?? summary?.[key] ?? 0)}
            </span>
          </span>
        ))}
        <span className="ml-auto text-neutral-400">
          Total:{" "}
          <span className="font-semibold text-neutral-200 tabular-nums">
            {Number(summary?.total ?? 0)}
          </span>
        </span>
      </div>
    ) : null;

  if (loading) {
    return (
      <>
        <SummaryStrip />
        <div className="p-8 text-neutral-300">Loading takedown requests…</div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <SummaryStrip />
        <div className="p-8 text-red-300">{error}</div>
      </>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <>
        <SummaryStrip />
        <div className="p-8 text-neutral-300">
          No takedown requests filed yet.
        </div>
      </>
    );
  }
  return (
    <>
      <SummaryStrip />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-neutral-300">
            <tr className="border-b border-sky-900/40">
              <th className="px-4 py-3 text-left font-semibold">Impersonates</th>
              <th className="px-4 py-3 text-left font-semibold">Detected Domain</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-left font-semibold">Type</th>
              <th className="px-4 py-3 text-left font-semibold">Last Updated</th>
              <th className="px-4 py-3 text-left font-semibold">Filed</th>
              <th className="px-4 py-3 text-right font-semibold w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {rows.map((row) => (
              <tr key={row.id} className="group hover:bg-white/[0.03] transition-colors">
                <td className="px-4 py-3 align-middle text-sky-200 text-xs">
                  <span className="inline-flex items-center gap-1">
                    <span>{row.original_entry || "—"}</span>
                    <CopyButton value={row.original_entry} />
                  </span>
                </td>
                <td className="px-4 py-3 align-middle font-medium text-white">
                  <span className="inline-flex items-center gap-1">
                    <span>{row.detected_domain || "—"}</span>
                    <CopyButton value={row.detected_domain} />
                  </span>
                </td>
                <td className="px-4 py-3 align-middle">
                  {(() => {
                    const s = String(row.status || "").toLowerCase();
                    const phaseTs =
                      s === "completed" ? row.completed_at :
                      s === "rejected"  ? row.rejected_at  :
                      s === "submitted" ? row.submitted_at :
                      (s === "info needed" || s === "info_needed") ? row.info_needed_at :
                      (s === "cancelled" || s === "canceled") ? row.cancelled_at :
                      null;
                    return (
                      <div className="flex flex-col items-start gap-0.5">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs capitalize ${statusTone(row.status)}`}
                          title={[
                            row.submitted_at && `Submitted: ${fmtTs(row.submitted_at)}`,
                            row.info_needed_at && `Info Needed: ${fmtTs(row.info_needed_at)}`,
                            row.completed_at && `Completed: ${fmtTs(row.completed_at)}`,
                            row.rejected_at && `Rejected: ${fmtTs(row.rejected_at)}`,
                            row.cancelled_at && `Cancelled: ${fmtTs(row.cancelled_at)}`,
                          ].filter(Boolean).join("\n") || undefined}
                        >
                          {row.status || "unknown"}
                        </span>
                        {phaseTs && (
                          <span className="text-[10px] text-neutral-400 tabular-nums">
                            {fmtTs(phaseTs)}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 align-middle">
                  {(() => {
                    const t = String(row.type || "").toLowerCase();
                    if (t === "adhoc" || t === "ad hoc" || t === "ad_hoc") {
                      return (
                        <span className="inline-flex items-center rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-violet-200">
                          Ad Hoc
                        </span>
                      );
                    }
                    if (t === "inhouse" || t === "in_house" || t === "in house") {
                      return (
                        <span className="inline-flex items-center rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-sky-200">
                          In-House
                        </span>
                      );
                    }
                    return <span className="text-neutral-500 text-xs">—</span>;
                  })()}
                </td>
                <td className="px-4 py-3 align-middle text-neutral-300 tabular-nums">
                  {row.status_updated_at ? fmtTs(row.status_updated_at) : "—"}
                </td>
                <td className="px-4 py-3 align-middle text-neutral-300 tabular-nums">
                  {fmtTs(row.created_at)}
                </td>
                <td className="px-4 py-3 align-middle text-right">
                  <button
                    type="button"
                    onClick={() => onOpenDetail?.(row)}
                    className="rounded-lg border border-sky-600/40 bg-sky-500/10 px-3 py-1.5 text-xs text-sky-200 hover:bg-sky-500/20 transition-colors"
                  >
                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function TakedownRequestDetailPanel({ row, onClose }) {
  if (!row) return null;
  const fmtTs = (ts) => (ts ? String(ts).replace("T", " ").slice(0, 19) : null);
  const status = String(row.status || "").toLowerCase();
  const isInfoNeeded = status === "info needed" || status === "info_needed";
  const isCancelled = status === "cancelled" || status === "canceled";
  const tone =
    status === "completed" ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30" :
    status === "submitted" ? "bg-sky-500/15 text-sky-200 border-sky-500/30" :
    status === "pending"   ? "bg-amber-500/15 text-amber-200 border-amber-500/30" :
    status === "rejected"  ? "bg-rose-500/15 text-rose-200 border-rose-500/30" :
    isInfoNeeded           ? "bg-indigo-500/15 text-indigo-200 border-indigo-500/30" :
    isCancelled            ? "bg-slate-500/15 text-slate-300 border-slate-500/30" :
                             "bg-neutral-700/30 text-neutral-300 border-neutral-700";

  const timeline = [
    { key: "created",   label: "Created",   ts: row.created_at,   tone: "text-neutral-300" },
    { key: "submitted", label: "Submitted", ts: row.submitted_at, tone: "text-sky-300" },
    isInfoNeeded           && { key: "info_needed", label: "Info Needed", ts: row.info_needed_at, tone: "text-indigo-300" },
    status === "completed" && { key: "completed", label: "Completed", ts: row.completed_at, tone: "text-emerald-300" },
    status === "rejected"  && { key: "rejected",  label: "Rejected",  ts: row.rejected_at,  tone: "text-rose-300" },
    isCancelled            && { key: "cancelled", label: "Cancelled", ts: row.cancelled_at, tone: "text-slate-300" },
  ].filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-[80] flex"
      role="dialog"
      aria-modal="true"
      aria-label="Takedown request details"
    >
      <div
        className="flex-1 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <aside className="relative w-full max-w-[640px] h-full bg-[#0a1224] border-l border-sky-900/40 overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-5 py-4 border-b border-sky-900/40 bg-[#0a1224]">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500">
              Takedown Request
            </div>
            <h2 className="mt-1 text-lg font-semibold text-white truncate" title={row.detected_domain}>
              {row.detected_domain || "—"}
            </h2>
            <div className="mt-1 text-xs text-sky-200/80">
              Impersonates <span className="font-medium">{row.original_entry || "—"}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-5">
          {/* Status badge */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">Status</div>
            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs capitalize ${tone}`}>
              {row.status || "unknown"}
            </span>
            {row.is_active === false && (
              <span className="ml-2 text-[11px] text-neutral-500">
                Archived / superseded
              </span>
            )}
          </div>

          {/* Timeline */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">Lifecycle</div>
            <ol className="relative border-l border-sky-900/40 ml-1 space-y-3">
              {timeline.map((t) => (
                <li key={t.key} className="ml-3">
                  <span className={`absolute -left-1.5 mt-1 h-2.5 w-2.5 rounded-full ring-2 ring-[#0a1224] ${
                    t.ts ? "bg-sky-400" : "bg-neutral-700"
                  }`} />
                  <div className={`text-sm ${t.tone}`}>{t.label}</div>
                  <div className="text-xs text-neutral-400 tabular-nums">
                    {fmtTs(t.ts) || <span className="text-neutral-600">— pending —</span>}
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Notes */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">Your Notes / evidence</div>
            <div className="rounded-lg border border-sky-900/30 bg-white/[0.02] p-3 text-sm text-neutral-200 whitespace-pre-wrap break-words">
              {row.notes ? row.notes : <span className="text-neutral-500">No notes provided.</span>}
            </div>
          </div>

          {/* Rejection reason — only when rejected and the field has content */}
          {status === "rejected" && row.rejection_note && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-rose-300/80 mb-1.5">
                Rejection reason
              </div>
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-100/90 whitespace-pre-wrap break-words">
                {row.rejection_note}
              </div>
            </div>
          )}

          {/* Info needed — what the team needs from the customer */}
          {isInfoNeeded && row.more_info_note && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-indigo-300/80 mb-1.5">
                Information needed from you
              </div>
              <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3 text-sm text-indigo-100/90 whitespace-pre-wrap break-words">
                {row.more_info_note}
                <div className="mt-3 pt-3 border-t border-indigo-500/20 text-xs text-indigo-200/80 not-italic">
                  Please reply directly to the email we sent, or email{" "}
                  <a
                    href={`mailto:notices@whiteintel.io?subject=${encodeURIComponent(`Takedown case #${row.id}`)}`}
                    className="text-indigo-200 hover:text-white underline-offset-2 hover:underline font-medium"
                  >
                    notices@whiteintel.io
                  </a>{" "}
                  with case ID{" "}
                  <span className="font-mono text-indigo-100">#{row.id}</span>{" "}
                  in the subject.
                </div>
              </div>
            </div>
          )}

          {/* Cancellation note */}
          {isCancelled && row.cancel_note && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-300/80 mb-1.5">
                Cancellation note
              </div>
              <div className="rounded-lg border border-slate-500/30 bg-slate-500/5 p-3 text-sm text-slate-100/90 whitespace-pre-wrap break-words">
                {row.cancel_note}
              </div>
            </div>
          )}

          {/* Additional notes from the takedown team */}
          {row.additional_note && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">
                Additional notes from team
              </div>
              <div className="rounded-lg border border-sky-900/30 bg-white/[0.02] p-3 text-sm text-neutral-200 whitespace-pre-wrap break-words">
                {row.additional_note}
              </div>
            </div>
          )}

          {/* Provider / contact */}
          <div className="grid grid-cols-1 gap-3">
            <KvInline label="Registrar" mono>
              {row.registrar ? <span className="capitalize">{row.registrar}</span> : "—"}
            </KvInline>
            <KvInline label="Abuse contact">
              {row.abuse_email ? (
                <a
                  href={`mailto:${row.abuse_email}`}
                  className="text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline"
                >
                  {row.abuse_email}
                </a>
              ) : (
                "—"
              )}
            </KvInline>
            <KvInline label="Filed by">
              {row.requester_email || "—"}
            </KvInline>
            {row.lookalike_domain_id && (
              <KvInline label="Detected lookalike ID" mono>
                #{row.lookalike_domain_id}
              </KvInline>
            )}
            <KvInline label="Last status update">
              <span className="tabular-nums">{fmtTs(row.status_updated_at) || "—"}</span>
            </KvInline>
          </div>
        </div>
      </aside>
    </div>
  );
}

function SidebarSpinBtn({ onClick, loading, icon, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      aria-busy={loading}
      title={title}
      aria-label={title}
      className="inline-flex items-center justify-center rounded-md border border-sky-600/40 bg-sky-500/10 p-1.5 text-sky-200 hover:bg-sky-500/20 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
    >
      {loading ? (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      ) : (
        <span className="flex items-center text-sky-300">{icon}</span>
      )}
    </button>
  );
}

function SidebarStatusBanner({ status, onDismiss }) {
  if (!status) return null;
  const tone =
    status.type === "success"
      ? "border-green-500/30 bg-green-500/10 text-green-200"
      : status.type === "error"
      ? "border-red-500/30 bg-red-500/10 text-red-200"
      : "border-yellow-500/30 bg-yellow-500/10 text-yellow-200";
  return (
    <div className={`mx-5 mt-3 flex items-center justify-between rounded-md border px-3 py-2 text-sm ${tone}`}>
      <span>
        {status.message}
        {status.issueUrl && (
          <>
            {" — "}
            <a href={status.issueUrl} target="_blank" rel="noreferrer" className="underline">
              Open in Jira
            </a>
          </>
        )}
      </span>
      <button onClick={onDismiss} className="ml-3 text-xs text-neutral-400 hover:text-white">
        Dismiss
      </button>
    </div>
  );
}